// Step by step engraving form for quaichs, whisky glasses, hip flasks, glass awards and every other engraved product ('generic'), layered over the engraving inputs of
// assets/engraving-options.js (window.WalEngraving), which owns the fields, the price and the cart lines.
// Each product type has its own profile (KINDS): wording, lines, occasions, lettering order, toasts and preview;
// PRODUCT_SPACE narrows it for products with less room to engrave. Products without a profile of their own (trophies, medals, wine glasses,
// decanters, tankards, sgian dubhs...) get a generic one: lines, occasions and wording that suit what they are (GENERIC_PROFILES).
// The inputs stay the source of truth for the cart properties; this form fills them in.
// Glass awards also get a "Several awards" mode (AwardBatch below; the pure parts are in award-batch-text.js).
// If anything here fails, the enhancement removes itself and the simple form of engraving-options.js is left as it was.
(() => {
  const FIELD_KEYS = ['engraving', 'font', 'text', 'logo', 'notes', 'infill'];
  const MAX_LINES = 6;
  // Glass quaichs are engraved on the outside of a narrow bowl: two lines on average.
  const GLASS_MAX_LINES = 2;
  const LONG_LINE = 30;
  const TEXT_ERROR = "Please add the words you'd like engraved.";
  const MAX_LINE_LENGTH = 80;
  const DRAFT_AGE = 30 * 24 * 60 * 60 * 1000;

  const FONT_FAMILIES = [
    ['franklin', "'Wal Libre Franklin', 'Helvetica Neue', Arial, sans-serif"],
    ['garamond', "'Wal EB Garamond', Garamond, Georgia, serif"],
    ['vanessa', "'Wal Dancing Script', cursive", { fontWeight: '600' }],
    ['victoria', "'Wal Great Vibes', cursive"],
    ['us block', "'Helvetica Neue', Helvetica, Arial, sans-serif", { fontWeight: '300', letterSpacing: '0.06em' }],
    ['roman', "'Times New Roman', Times, Georgia, serif"],
    ['fraktur', "'Wal UnifrakturMaguntia', serif"],
    ['aref ruqaa', "'Wal Aref Ruqaa', serif"],
    ['arima', "'Wal Arima', serif"],
    ['bahnschrift', "'Wal Bahnschrift', 'Arial Narrow', sans-serif"],
    ['cambria', "'Wal Cambria', Georgia, serif"],
    ['celtic garamond', "'Wal Celtic Garamond', serif"],
    ['gabriola', "'Wal Gabriola', Georgia, serif"],
    ['cherry swash', "'Wal Cherry Swash', cursive"],
    ['courgette', "'Wal Courgette', cursive"],
    ['lucida handwriting', "'Wal Lucida Handwriting', cursive"],
    ['niconne', "'Wal Niconne', cursive"],
    ['tangerine', "'Wal Tangerine', cursive"],
    ['snell roundhand', "'Wal Roundhand', cursive"],
    ['roundhand', "'Wal Roundhand', cursive"],
    ['century', "'Century Gothic', Futura, 'Helvetica Neue', Arial, sans-serif"],
  ];
  const DEFAULT_FAMILY = "'Wal EB Garamond', Georgia, serif";

  // Most chosen first, from engraved orders since 2024. Unlisted fonts keep the option set's order after these.
  const FONT_POPULARITY = [
    'vanessa', 'victoria', 'garamond', 'franklin', 'aref ruqaa', 'snell roundhand free', 'roundhand', 'roman', 'courgette', 'gabriola',
    'cambria', 'celtic garamond', 'bahnschrift', 'tangerine', 'arima medium', 'niconne', 'gaelic italic', 'fraktur out',
    'century os', 'us block',
  ];
  const fontDisplayName = (value) => value.replace(/\s+\d+\s*(L|Line)s?\s*$/i, '').trim();
  // From engraved whisky glass orders since July 2025 (35); the glasses' font list has no Vanessa or Victoria.
  const GLASS_FONT_POPULARITY = [
    'roundhand', 'snell roundhand free', 'aref ruqaa', 'celtic garamond', 'cambria', 'arima medium', 'courgette', 'gabriola',
    'niconne', 'tangerine', 'bahnschrift', 'cherry swash',
  ];
  // Set once per page from the product's profile in start()
  let fontPopularity = FONT_POPULARITY;
  const fontRank = (value) => {
    const rank = fontPopularity.indexOf(fontDisplayName(value).toLowerCase());
    return rank < 0 ? fontPopularity.length : rank;
  };
  let fontDefaultDone = false;
  let applyingDefaultFont = false;
  let restoringDraft = false;
  // The engraving font itself where we have the file, otherwise the closest free web font, with any weight or spacing that brings it nearer the shop's sample.
  const previewFont = (value) => {
    const name = fontDisplayName(value || '').toLowerCase();
    const match = FONT_FAMILIES.find(([key]) => name.startsWith(key));
    return { fontFamily: match ? match[1] : DEFAULT_FAMILY, fontWeight: '400', letterSpacing: 'normal', ...(match && match[2]) };
  };

  const today = new Date();
  const monthYear = today.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  const ordinal = (day) => {
    const suffix = day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th';
    return `${day}${suffix}`;
  };
  const twoWeeksAhead = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000);
  const exampleDate = `${ordinal(twoWeeksAhead.getDate())} ${twoWeeksAhead.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}`;

  // Ordered by how often each comes up in past quaich engravings; placeholders follow the most common wording.
  const OCCASIONS = [
    { id: 'wedding', label: 'Wedding', lines: [['Names', 'Sarah & James'], ['Date', exampleDate], ['Extra line (optional)', 'Slàinte mhath']] },
    { id: 'birthday', label: 'Birthday', lines: [['Message', 'Happy 60th Birthday John'], ['From', 'Love from Mum & Dad'], ['Extra line (optional)', monthYear]] },
    { id: 'thanks', label: 'Retirement', lines: [['Name', 'John Smith'], ['Message', 'Thank you for 30 years of service'], ['From', 'From all at the club']] },
    { id: 'friendship', label: 'Friendship', lines: [['To', 'To Alasdair'], ['Message', 'In friendship'], ['From', 'From Iain']] },
    { id: 'christening', label: 'Christening', lines: [['Name', 'Olivia Grace'], ['Message', 'On your Christening'], ['Date', exampleDate]], glassLines: [['Name', 'Olivia Grace'], ['Date', exampleDate]] },
    { id: 'other', label: 'Other', lines: [['Line 1', 'Your message'], ['Line 2', ''], ['Line 3', '']] },
  ];

  // One tap adds the toast spelled right; Slàinte mhath is on about 1 in 16 quaich engravings and often typed without the accent.
  const TOASTS = [
    ['Slàinte mhath', 'good health'],
    ['Gràdh gu bràth', 'love forever'],
  ];

  // Whisky glasses, from engraved orders since July 2025: wedding parties and plain names lead, then birthdays; 1 to 4 short lines.
  const year = today.getFullYear();
  const twoDigits = (number) => String(number).padStart(2, '0');
  const shortDate = `${twoDigits(twoWeeksAhead.getDate())}.${twoDigits(twoWeeksAhead.getMonth() + 1)}.${String(twoWeeksAhead.getFullYear()).slice(2)}`;
  const GLASS_OCCASIONS = [
    { id: 'wedding', label: 'Wedding', lines: [['Role', 'Best Man'], ['Name', 'Callum'], ['Date or toast (optional)', shortDate]] },
    { id: 'name', label: 'Name', lines: [['Name', 'James Murray'], ['Extra line (optional)', 'Slàinte mhath']] },
    { id: 'birthday', label: 'Birthday', lines: [['Message', 'Happy 60th'], ['Name', 'Dad'], ['Extra line (optional)', `${year - 60} – ${year}`]] },
    { id: 'thanks', label: 'Thank you', lines: [['Name', 'David Wilson'], ['Message', '32 Years of Service'], ['Extra line (optional)', 'Thank You']] },
    { id: 'dad', label: 'For Dad', lines: [['Message', "Dad's Dram"], ['From', 'Love, Freya & Noah']] },
    { id: 'other', label: 'Other', lines: [['Line 1', 'Your message'], ['Line 2', ''], ['Line 3', '']] },
  ];

  // Hip flasks: too few web orders to go on (8 engraved), so the occasions follow the collection page and the example photos.
  const FLASK_OCCASIONS = [
    { id: 'wedding', label: 'Wedding', lines: [['Role', 'Best Man'], ['Name', 'Callum'], ['Date or toast (optional)', shortDate]] },
    { id: 'name', label: 'Name', lines: [['Name', 'James Murray'], ['Extra line (optional)', 'Slàinte mhath']] },
    { id: 'birthday', label: 'Birthday', lines: [['Name', 'Ross'], ['Message', '21st Birthday'], ['Date (optional)', exampleDate]] },
    { id: 'thanks', label: 'Thank you', lines: [['Name', 'David Wilson'], ['Message', '32 Years of Service'], ['Extra line (optional)', 'Thank You']] },
    { id: 'dad', label: 'For Dad', lines: [['Message', 'Dad'], ['From', 'Love, Freya & Noah']] },
    { id: 'other', label: 'Other', lines: [['Line 1', 'Your message'], ['Line 2', ''], ['Line 3', '']] },
  ];

  // Glass awards, from 36 engraved web orders (Aug 2025 to Sep 2026): award title, name, reason, organisation and date, 3 to 9 lines.
  const season = today.getMonth() >= 7 ? `${year}/${String(year + 1).slice(2)}` : `${year - 1}/${String(year).slice(2)}`;
  const AWARD_OCCASIONS = [
    { id: 'service', label: 'Long service', lines: [['Name', 'Margaret Brown'], ['Message', 'Thank you for 20 years of dedicated service'], ['Organisation (optional)', 'Clydebank Housing Association'], ['Year (optional)', String(year)]] },
    { id: 'retirement', label: 'Retirement', lines: [['Name', 'David Wilson'], ['Message', 'Enjoy your well earned retirement'], ['From (optional)', 'From all your colleagues'], ['Dates (optional)', `1991 – ${year}`]] },
    { id: 'sports', label: 'Sports & clubs', lines: [['Award', 'Player of the Year'], ['Name', 'Jamie Wilson'], ['Club or event', 'Clydebank Amateur FC'], ['Season (optional)', season]] },
    { id: 'achievement', label: 'Achievement', lines: [['Award', 'Winner'], ['Event', 'Pitch Final'], ['Name (optional)', 'Jamie Wilson'], ['Year (optional)', String(year)]] },
    { id: 'thanks', label: 'Thank you', lines: [['To', 'Thank you Ian'], ['Message', 'For all your hard work and support'], ['From', 'From all the team']] },
    { id: 'other', label: 'Other', lines: [['Line 1', 'Your message'], ['Line 2', ''], ['Line 3', '']] },
  ];
  // From web award orders: Aref Ruqaa and Cambria lead, then the rest in the option set's order
  const AWARD_FONT_POPULARITY = [
    'aref ruqaa', 'cambria', 'bahnschrift', 'gabriola', 'arima medium', 'niconne', 'courgette', 'roundhand', 'snell roundhand free', 'celtic garamond',
  ];
  const INFILL_COLOURS = [['gold', '#c9a14a'], ['silver', '#c0c4c8'], ['copper', '#b8734a']];
  const infillColour = (value) => {
    const found = INFILL_COLOURS.find(([key]) => String(value).toLowerCase().includes(key));
    return found ? found[1] : null;
  };
  const isNameLabel = (label) => /^Name\b/.test(label);

  // Everything else that is engraved (88 trophies and medals, 116 wine, champagne and other glasses, decanters and vases, sgian dubhs...): the
  // wording is chosen by what the piece is, from its collection (quaich-form-assets.liquid sets config.profile, sport, family and noun).
  const SPORT_AWARDS = {
    golf: { club: 'Clydebank Golf Club', title: 'Club Champion', special: ['Nearest the Pin', 'Nearest the Pin', 'Club Open'] },
    football: { club: 'Clydebank Amateur FC', title: 'Player of the Year', special: ['Top scorer', 'Top Scorer', 'Season'] },
    dance: { club: 'Clydebank Dance Academy', title: 'Best Dancer', special: ['Most improved', 'Most Improved', 'Annual Show'] },
  };
  const trophyOccasions = (sport) => {
    const award = SPORT_AWARDS[sport] || { club: 'Clydebank Amateur FC', title: 'Winner', special: ['Achievement', 'Outstanding Achievement', 'Annual Awards'] };
    return [
      { id: 'winner', label: 'Winner', lines: [['Award', award.title], ['Name', 'Jamie Wilson'], ['Club or event (optional)', award.club], ['Year (optional)', String(year)]] },
      { id: 'special', label: award.special[0], lines: [['Award', award.special[1]], ['Name', 'Jamie Wilson'], ['Event (optional)', award.special[2] === 'Season' ? season : award.special[2]], ['Year (optional)', String(year)]] },
      { id: 'runner', label: 'Runner-up', lines: [['Award', 'Runner Up'], ['Name', 'Jamie Wilson'], ['Club or event (optional)', award.club], ['Year (optional)', String(year)]] },
      { id: 'thanks', label: 'Thank you', lines: [['To', 'Thank you Ian'], ['Message', 'For all your hard work'], ['From (optional)', 'From all the team']] },
      { id: 'other', label: 'Other', lines: [['Line 1', 'Your message'], ['Line 2', ''], ['Line 3', '']] },
    ];
  };
  const SMALL_OCCASIONS = [
    { id: 'initials', label: 'Initials', lines: [['Initials', 'J S M'], ['Date (optional)', shortDate]] },
    { id: 'name', label: 'Name', lines: [['Name', 'James Murray'], ['Extra line (optional)', 'Slàinte mhath']] },
    { id: 'wedding', label: 'Wedding', lines: [['Role', 'Best Man'], ['Name', 'Callum'], ['Date (optional)', shortDate]] },
    { id: 'other', label: 'Other', lines: [['Line 1', 'Your message'], ['Line 2', '']] },
  ];
  // Trophies and medals carry names and titles; glasses and decanters are gifts (weddings, birthdays, retirements); sgian dubhs and cufflinks hold a name or initials.
  const GENERIC_PROFILES = {
    trophy: (config) => ({ maxLines: 4, occasions: trophyOccasions(config.sport), toasts: [] }),
    medal: (config) => ({ maxLines: 3, occasions: trophyOccasions(config.sport), toasts: [] }),
    drinks: () => ({ maxLines: 4, occasions: GLASS_OCCASIONS, toasts: TOASTS.slice(0, 1) }),
    vessel: () => ({ maxLines: 6, occasions: OCCASIONS, toasts: TOASTS }),
    small: () => ({ maxLines: 2, occasions: SMALL_OCCASIONS, toasts: TOASTS.slice(0, 1) }),
    gift: () => ({ maxLines: 6, occasions: OCCASIONS, toasts: TOASTS }),
  };

  // Products with less room to engrave than their type allows, judged from the product photos (Oct 2026).
  const SLEEVED = { lines: 2, shape: 'sleeved', note: 'On the clear band above the pewter · up to 2 short lines' };
  const NOSING = { lines: 3, note: 'On the bowl of the glass · up to 3 short lines' };
  const PRODUCT_SPACE = {
    'stag-and-thistle-whisky-glass': SLEEVED,
    'personalised-stag-thistle-boxed-whisky-gift': SLEEVED,
    'irish-harp-and-shamrocks-glass-and-pewter-whisky-glass': SLEEVED,
    'highland-cow-pewter-whisky-glass': SLEEVED,
    'glass-and-pewter-welsh-dragon-whisky-glass': SLEEVED,
    'diamond-whisky-glass': SLEEVED,
    'borough-shot-glass-75ml-clear': { lines: 2, note: 'A small glass · up to 2 short lines' },
    'glencairn-tasting-whisky-glass': NOSING,
    'whisky-tasting-glass-185ml': NOSING,
    'nude-whisky-glass-190ml-medium-glass': NOSING,
    'vinoteque-snifter-170ml': NOSING,
    'stag-whisky-glass-pair': { note: 'On the clear glass opposite each stag · up to 4 short lines per glass' },
    'stag-whisky-glass-and-hipflask': { note: 'On the glass, frosted · flask too? Add a note for the engraver' },
    '6oz-hip-flask-celtic-casting': { lines: 2, shape: 'band', note: 'Above the Celtic band · up to 2 short lines' },
    'oblong-2-celtic-band-flask': { lines: 3, shape: 'bands', note: 'Between the two Celtic bands · up to 3 short lines' },
    'celtic-frame-round-hip-flask-6oz': { lines: 3, shape: 'frame', note: 'In the centre of the Celtic frame · up to 3 short lines' },
    'plain-round-flask-6oz': { shape: 'round' },
    'pewter-celtic-hip-flask': { shape: 'band-base' },
    'bronze-cushion-flask': { shape: 'bronze' },
    'handcrafted-stag-head-glass-and-pewter-hip-flask': { lines: 2, note: 'Up to 2 short lines on this flask' },
  };

  // A logo or crest is part of the engraving, on its own or with words, added from one card under the lines.
  // With no words, the engraver gets LOGO_ONLY so the required text is honest rather than blank.
  const LOGO_ONLY = 'Logo only, no words';
  const SHIELD = '<svg viewBox="0 0 24 28" aria-hidden="true" focusable="false"><path d="M12 1.5 2.5 5v8.2c0 6.1 4 10.6 9.5 13.3 5.5-2.7 9.5-7.2 9.5-13.3V5z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M12 7v13M7 12h10" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
  const shieldIcon = (className) => {
    const node = document.createElement('span');
    node.className = className;
    node.innerHTML = SHIELD;
    return node;
  };

  // config.kind comes from quaich-form-assets.liquid; config.glass (glass quaich) and config.placement ('base') narrow it further.
  const KINDS = {
    quaich: { noun: 'quaich', maxLines: MAX_LINES, occasions: OCCASIONS, toasts: TOASTS, fonts: FONT_POPULARITY },
    'whisky-glass': { noun: 'glass', maxLines: 4, occasions: GLASS_OCCASIONS, toasts: TOASTS.slice(0, 1), fonts: GLASS_FONT_POPULARITY },
    // Flasks use the quaichs' metal engraving lettering, so the quaich order of popularity applies
    'hip-flask': { noun: 'flask', maxLines: 4, occasions: FLASK_OCCASIONS, toasts: TOASTS.slice(0, 1), fonts: FONT_POPULARITY },
    'glass-award': { noun: 'award', maxLines: 8, occasions: AWARD_OCCASIONS, toasts: [], fonts: AWARD_FONT_POPULARITY },
  };
  // No lettering order of popularity for the generic profiles: the option set's own order (Franklin and Garamond first on metal) is what
  // the 60 engraved web orders on these products chose most, and there are too few to rank the rest.
  const kindOf = (config) => {
    if (config.kind !== 'generic') return KINDS[config.kind] || KINDS.quaich;
    const profile = (GENERIC_PROFILES[config.profile] || GENERIC_PROFILES.gift)(config);
    return { noun: config.noun || 'piece', fonts: [], ...profile };
  };
  const plainLetters = (text) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z]/g, '');

  const element = (tag, attributes = {}, children = []) => {
    const node = document.createElement(tag);
    Object.entries(attributes).forEach(([key, value]) => {
      if (key === 'text') node.textContent = value;
      else if (value !== false && value != null) node.setAttribute(key, value === true ? '' : value);
    });
    children.forEach((child) => node.append(child));
    return node;
  };

  // The dispatch box carries the Engraving form setting for the extra days a logo adds before posting
  const logoDelay = () => {
    const panel = document.querySelector('[data-wal-dispatch-settings]');
    const days = panel ? parseInt(panel.dataset.logoDays, 10) : 0;
    if (!(days > 0)) return 'This can add a little time before we post your order.';
    return `This adds ${days} working ${days === 1 ? 'day' : 'days'} before we post your order.`;
  };

  const fieldLabel = (wrapper) => wrapper && wrapper.querySelector('.wal-field__label label');
  // Only writes when the text differs, so re-running can't loop through an observer.
  const setText = (node, text) => {
    if (node && node.textContent !== text) node.textContent = text;
  };
  const setLabelText = (wrapper, text) => setText(fieldLabel(wrapper), text);

  // Replaces the visible text of a radio's label, keeping the input itself.
  const setChoiceText = (input, nodes) => {
    const label = input.closest('label');
    if (!label || label.querySelector('.wal-choice__text')) return;
    [...label.childNodes].forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) child.remove();
    });
    label.classList.add('wal-choice');
    label.append(element('span', { class: 'wal-choice__text' }, nodes));
  };

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const dispatchInputEvents = (input) => {
    ['input', 'keyup', 'change'].forEach((type) => input.dispatchEvent(new Event(type, { bubbles: true })));
  };

  // Storage can be unavailable (private browsing, full or switched off); the form works the same without it.
  const storage = (action) => {
    try {
      return action();
    } catch (error) {
      return null;
    }
  };

  const readDraft = (key) => {
    const draft = storage(() => JSON.parse(localStorage.getItem(key)));
    const age = draft && Date.now() - draft.savedAt;
    if (!draft) return null;
    if (Number.isFinite(age) && age >= 0 && age < DRAFT_AGE) return draft;
    storage(() => localStorage.removeItem(key));
    return null;
  };

  class QuaichEngraving {
    constructor(container, config) {
      this.container = container;
      this.config = config;
      this.kind = kindOf(config);
      this.space = PRODUCT_SPACE[config.handle] || {};
      this.maxLines = config.glass ? GLASS_MAX_LINES : this.space.lines || (config.placement === 'base' ? 3 : this.kind.maxLines);
      this.onChange = () => {};
      this.onTrack = () => {};
      this.occasion = this.kind.occasions[0].id;
      this.isAward = config.kind === 'glass-award';
      // Glass awards only: the "Several awards" mode and the name-larger tick
      this.batch = null;
      this.lineValues = [];
      this.pairMode = 'same';
      this.secondLineValues = [];
      this.extrasOpen = false;
    }

    // The logo file for the preview: its name and picture (a local copy while it uploads), from the shared uploader (artwork-upload.js)
    get logoFile() {
      const { status, name, preview, photo } = window.WalArtwork.state;
      return name && (status === 'uploading' || status === 'done') ? { name, url: preview, photo } : null;
    }

    // As many line boxes as the chosen occasion suggests (quaichs: always 3, glass quaichs 2)
    get baseLines() {
      return Math.min(Math.max(this.occasionLines().length, 1), this.maxLines);
    }

    find() {
      const fields = window.WalEngraving && window.WalEngraving.fields;
      if (!fields) return false;
      this.fields = Object.fromEntries(FIELD_KEYS.map((key) => [key, fields[key] || null]));
      this.engravingInputs = this.fields.engraving ? [...this.fields.engraving.querySelectorAll('input[type="radio"]')] : [];
      this.textarea = this.fields.text && this.fields.text.querySelector('textarea');
      return Boolean(this.engravingInputs.length && this.textarea);
    }

    checked(name) {
      return this.fields[name] && this.fields[name].querySelector('input[type="radio"]:checked');
    }

    build() {
      this.container.classList.add('wal-io');
      Object.entries(this.fields).forEach(([name, wrapper]) => wrapper && wrapper.classList.add(`wal-f-${name}`));
      if (this.fields.notes) this.fields.notes.classList.add('wal-extra');
      if (this.fields.logo) this.fields.logo.classList.add('wal-app-hidden');

      this.relabel();
      this.keepOutOfKeyboardPath();
      this.buildLines();
      this.buildPreview();
      if (this.config.pair) this.buildPair();
      this.buildLogo();
      this.bindArtwork();
      if (this.isAward) {
        if (!window.WalAwardBatch) throw new Error('award-batch-text.js did not load');
        this.batch = new AwardBatch(this);
        this.batch.build();
      }
      this.buildExtrasToggle();
      this.buildPriceSummary();
      this.matchTabOrderToScreen();

      this.readTextarea();
      this.renderLines();
      this.update();
    }

    relabel() {
      this.relabelEngraving();
      this.relabelFonts();
      this.relabelInfill();
      this.relabelExtras();
    }

    // The fields are laid out by CSS order (quaich-form.css), but Tab and screen readers follow the page's own order, which was the option
    // set's (lettering before the words). Moving the blocks into their on-screen order makes the three agree. They post by form="...", so
    // where they sit in the page does not matter to the cart.
    matchTabOrderToScreen() {
      const blocks = [...this.container.children].map((node, index) => ({ node, index, order: parseInt(getComputedStyle(node).order, 10) || 0 }));
      blocks.sort((a, b) => a.order - b.order || a.index - b.index);
      if (blocks.every((block, index) => block.index === index)) return;
      this.container.append(...blocks.map((block) => block.node));
    }

    // The real words box and the logo choice stay in the page as the cart's source of truth but are out of sight (the lines and the logo card
    // drive them), so Tab must not stop on them and a screen reader must not read them out as a second, unlabelled form
    keepOutOfKeyboardPath() {
      [this.textarea, ...this.logoInputs()].forEach((input) => {
        input.tabIndex = -1;
        input.setAttribute('aria-hidden', 'true');
      });
    }

    infillInputs() {
      return this.fields.infill ? [...this.fields.infill.querySelectorAll('input[type="radio"]')] : [];
    }

    // "Add Infill?" becomes colour choices with a swatch; the engraver still gets the option's own value
    relabelInfill() {
      if (!this.fields.infill) return;
      // Customers asked whether the colour is per order or per award: it's per award, so the heading says so
      const yes = this.infillInputs().find((input) => input.value !== 'No');
      const each = yes && (yes.value.match(/\+\s*(£\s*[\d.,]+)/) || [])[1];
      setLabelText(this.fields.infill, `Colour the lettering (optional)${each ? ` · ${each.replace(/\s/g, '')} per award` : ''}`);
      this.infillInputs().forEach((input) => {
        if (input.value === 'No') {
          setChoiceText(input, ['No colour']);
          return;
        }
        const price = (input.value.match(/\+\s*(£\s*[\d.,]+)/) || [])[1];
        const swatch = element('span', { class: 'wal-swatch', 'aria-hidden': 'true' });
        swatch.style.background = infillColour(input.value) || 'transparent';
        setChoiceText(input, [swatch, element('span', { class: 'wal-choice__label' }, [element('span', { text: input.value.replace(/\s*\(.*$/, '') }), price ? element('span', { class: 'wal-choice__price', text: `+${price.replace(/\s/g, '')}` }) : ''])]);
      });
    }

    // The colour of the chosen infill, for the preview words
    infillColour() {
      const chosen = this.checked('infill');
      return chosen && chosen.value !== 'No' ? infillColour(chosen.value) : null;
    }

    relabelEngraving() {
      setLabelText(this.fields.engraving, `Personalise your ${this.config.noun || this.kind.noun}`);
      this.engravingInputs.forEach((input) => {
        const price = (input.value.match(/\+\s*(£\s*[\d.,]+)/) || [])[1];
        if (input.value === 'No') setChoiceText(input, ['No engraving']);
        else setChoiceText(input, [this.config.pair ? 'Yes, engrave both ' : 'Yes, engrave it ', price ? element('span', { class: 'wal-choice__price', text: `+${price.replace(/\s/g, '')}` }) : '']);
      });
    }

    relabelFonts() {
      if (!this.fields.font) return;
      setLabelText(this.fields.font, 'Lettering');
      this.orderFonts();
      this.fields.font.querySelectorAll('input[type="radio"]').forEach((input) => {
        const name = fontDisplayName(input.value);
        if (input.getAttribute('aria-label') !== name) input.setAttribute('aria-label', name);
        const label = input.closest('label');
        if (!label) return;
        const caption = label.querySelector('.wal-swatch-name');
        if (caption) {
          setText(caption, name);
          return;
        }
        // Some renders put the name in a bare text node rather than a span.
        [...label.childNodes].forEach((child) => child.nodeType === Node.TEXT_NODE && child.textContent.trim() && child.remove());
        label.append(element('span', { class: 'wal-swatch-name', text: name }));
      });
      if (this.config.kind === 'generic') this.buildSamples();
    }

    // Generic profile: once there are words, each lettering card shows them (the first line) in that lettering instead of the sample
    // picture. The sample sits beside the picture and stays hidden until then, so no web font is fetched before it is needed.
    buildSamples() {
      this.fontInputs().forEach((input) => {
        const label = input.closest('label');
        if (!label || label.querySelector('.wal-swatch-sample')) return;
        const sample = element('span', { class: 'wal-swatch-sample', 'aria-hidden': 'true' }, [element('span', { class: 'wal-swatch-sample__words' })]);
        Object.assign(sample.style, previewFont(input.value));
        label.querySelector('.wal-swatch-img').after(sample);
      });
      this.samplesNote = element('p', { class: 'wal-note wal-samples-note', hidden: true, text: 'Shown with your words. We engrave in the exact lettering.' });
      this.fields.font.append(this.samplesNote);
      // The twelve lettering fonts are about 300 KB in all, so they are fetched only once the cards are near the screen, not as soon as a
      // word is typed at the top of a phone's form
      this.lettersNear = typeof IntersectionObserver === 'undefined';
      if (this.lettersNear) return;
      this.lettersWatch = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.lettersNear = true;
        this.lettersWatch.disconnect();
        this.renderSamples();
      }, { rootMargin: '300px 0px' });
      this.lettersWatch.observe(this.fields.font);
    }

    renderSamples() {
      if (!this.samplesNote) return;
      const first = this.lineValues.find((line) => line.trim());
      const words = first && this.lettersNear ? first.trim().slice(0, 40) : '';
      this.fields.font.classList.toggle('wal-lettering--words', Boolean(words));
      this.samplesNote.hidden = !words;
      this.fields.font.querySelectorAll('.wal-swatch-sample__words').forEach((node) => setText(node, words));
    }

    fontInputs() {
      return this.fields.font ? [...this.fields.font.querySelectorAll('input[type="radio"]')] : [];
    }

    // Moves the cards so the most popular lettering comes first, only when the order differs (avoids observer loops).
    orderFonts() {
      const cards = this.fontInputs().map((input) => input.closest('label')).filter(Boolean);
      const sorted = cards
        .map((card, index) => ({ card, index, rank: fontRank(card.querySelector('input').value) }))
        .sort((a, b) => a.rank - b.rank || a.index - b.index)
        .map((item) => item.card);
      // "Most popular" pill on the top card (drawn in CSS); toggle with force leaves the class untouched when unchanged
      cards.forEach((card) => card.classList.toggle('wal-font--popular', card === sorted[0] && fontRank(card.querySelector('input').value) < fontPopularity.length));
      if (sorted.every((card, index) => card === cards[index])) return;
      const parent = cards[0].parentElement;
      if (!cards.every((card) => card.parentElement === parent)) return;
      sorted.forEach((card) => parent.append(card));
    }

    popularFont() {
      return [...this.fontInputs()].sort((a, b) => fontRank(a.value) - fontRank(b.value))[0];
    }

    // Swaps the option set's automatic first font for the most popular one, once, before the customer picks anything.
    applyDefaultFont() {
      if (fontDefaultDone || restoringDraft || !this.engraving()) return;
      const inputs = this.fontInputs();
      if (!inputs.length) return;
      fontDefaultDone = true;
      const checked = inputs.find((input) => input.checked);
      const best = this.popularFont();
      const automatic = !checked || checked.value === window.WalEngraving.config.fonts[0].value;
      if (!automatic || !best || best === checked) return;
      applyingDefaultFont = true;
      best.click();
      applyingDefaultFont = false;
    }

    relabelExtras() {
      setLabelText(this.fields.logo, 'Add a logo or crest?');
      // The note's own label is not shown here: the "Add a note for the engraver" button above it is the label (buildExtrasToggle)
    }

    buildLines() {
      this.occasionButtons = this.kind.occasions.map((occasion) =>
        element('button', { type: 'button', class: 'wal-chip', 'data-wal-occasion': occasion.id, 'aria-pressed': 'false', text: occasion.label })
      );
      this.linesList = element('div', { class: 'wal-lines' });
      this.addLineButton = element('button', { type: 'button', class: 'wal-add-line link', 'data-wal-add-line': true, text: '+ Add another line' });
      this.toastButtons = this.kind.toasts.map(([phrase, meaning]) =>
        element('button', { type: 'button', class: 'wal-toast', 'data-wal-toast': phrase, 'aria-pressed': 'false', 'aria-label': `${phrase}, ${meaning}` }, [
          element('span', { class: 'wal-toast__text' }, [element('span', { class: 'wal-toast__phrase', text: phrase }), element('span', { class: 'wal-toast__meaning', text: meaning })]),
        ])
      );
      this.toastNote = element('p', { class: 'wal-note', hidden: true, text: 'No room for another line. Clear a line first.' });
      this.toasts = element('div', { class: 'wal-toasts', role: 'group', 'aria-label': 'Add a Gaelic toast' }, [
        element('span', { class: 'wal-toasts__label', text: 'Add a toast' }),
        ...this.toastButtons,
      ]);
      this.longNote = element('p', { class: 'wal-note', hidden: true, text: 'Long lines are engraved in smaller lettering.' });
      this.error = element('p', { class: 'wal-error', id: 'wal-error', role: 'alert', 'data-wal-error': true, hidden: true });

      this.chips = element('div', { class: 'wal-chips', role: 'group', 'aria-label': 'Occasion' }, this.occasionButtons);
      this.root = element('div', { class: 'wal-engrave', 'data-wal-engrave': true }, [
        element('p', { class: 'wal-engrave__heading', text: 'What would you like engraved?' }),
        this.chips,
        this.linesList,
        this.addLineButton,
        this.kind.toasts.length ? this.toasts : '',
        this.toastNote,
        this.longNote,
        this.error,
      ]);
      this.fields.text.append(this.root);

      this.occasionButtons.forEach((button) =>
        button.addEventListener('click', () => {
          this.occasion = button.dataset.walOccasion;
          // Blank boxes beyond what the new occasion needs go, so a two-line occasion shows two
          while (this.lineValues.length > this.baseLines && !this.lineValues[this.lineValues.length - 1].trim()) this.lineValues.pop();
          this.renderLines();
          this.update();
          this.onChange();
        })
      );
      this.toastButtons.forEach((button) => button.addEventListener('click', () => this.toggleToast(button.dataset.walToast)));
      this.addLineButton.addEventListener('click', () => {
        if (this.lineValues.length >= this.maxLines) return;
        this.lineValues.push('');
        this.renderLines();
        this.update();
        this.onChange();
        this.lineInputs[this.lineInputs.length - 1].focus();
      });
    }

    buildPair() {
      // The same two cards as One award / Several awards (real radios in labels, so keyboard, screen readers and taps all work)
      this.pairChoices = element('fieldset', { class: 'wal-mode wal-pair-choices' }, [element('legend', { class: 'visually-hidden', text: 'Engrave both glasses' })]);
      const cards = { same: ['Same on both', 'One set of words, engraved on each glass'], different: ['Different on each', 'Separate wording for Glass 1 and Glass 2'] };
      this.pairRadios = ['same', 'different'].map((mode) => {
        const input = element('input', { type: 'radio', name: 'wal-pair-mode', value: mode });
        input.addEventListener('change', () => {
          this.pairMode = mode;
          if (mode === 'different' && !this.secondLineValues.some((line) => line.trim())) this.secondLineValues = [...this.lineValues];
          this.renderSecond(); this.update(); this.onChange();
        });
        this.pairChoices.append(element('label', { class: 'wal-mode__card', 'data-wal-mode': mode }, [input, element('span', { class: 'wal-mode__title', text: cards[mode][0] }), element('span', { class: 'wal-mode__help', text: cards[mode][1] })]));
        return input;
      });
      this.root.querySelector('.wal-engrave__heading').after(this.pairChoices);
      this.firstHeading = element('h3', { class: 'wal-pair-heading' });
      this.linesList.before(this.firstHeading);
      this.secondGroup = element('div', { class: 'wal-pair-second' }, [element('h3', { class: 'wal-pair-heading', text: 'Glass 2 wording' })]);
      this.toastNote.after(this.secondGroup);
      this.pairHelp = element('p', { class: 'wal-note wal-pair-help', text: 'The standard engraving option covers both glasses. One lettering style and any shared logo apply to both. Quantity means complete pairs with these same instructions.' });
      this.chips.before(this.pairHelp);
      const bowl = this.preview.querySelector('.wal-preview__bowl');
      const other = bowl.cloneNode(true);
      this.secondPreviewText = other.querySelector('.wal-preview__text');
      const previews = element('div', { class: 'wal-pair-previews' }, [
        element('div', {}, [element('h4', { text: 'Glass 1' }), bowl]),
        element('div', {}, [element('h4', { text: 'Glass 2' }), other]),
      ]);
      this.preview.prepend(previews);
      this.renderSecond();
    }

    renderSecond() {
      if (!this.config.pair) return;
      this.pairRadios.forEach((input) => { input.checked = input.value === this.pairMode; });
      this.firstHeading.textContent = this.pairMode === 'same' ? 'Wording for both glasses' : 'Glass 1 wording';
      this.addLineButton.textContent = this.pairMode === 'same' ? '+ Add another line for both' : '+ Add another line to Glass 1';
      this.toasts.querySelector('.wal-toasts__label').textContent = this.pairMode === 'same' ? 'Add a toast to both' : 'Add a toast to Glass 1';
      this.secondGroup.hidden = this.pairMode !== 'different';
      this.secondGroup.querySelectorAll('.wal-line').forEach((node) => node.remove());
      this.secondInputs = Array.from({ length: this.maxLines }, (_, index) => {
        const id = `wal-glass-two-${index + 1}`;
        const input = element('input', { type: 'text', id, class: 'wal-line__input', maxlength: MAX_LINE_LENGTH, autocomplete: 'off', 'data-wal-glass-two': true });
        input.value = this.secondLineValues[index] || '';
        input.addEventListener('input', () => { this.secondLineValues[index] = input.value; this.hideError(); this.update(); this.onChange(); });
        input.addEventListener('keydown', (event) => { if (event.key === 'Enter') { event.preventDefault(); this.secondInputs[index + 1]?.focus(); } });
        this.secondGroup.append(element('div', { class: 'wal-line' }, [element('div', { class: 'wal-line__label' }, [element('label', { for: id, text: `Line ${index + 1}${index ? ' (optional)' : ''}` })]), input]));
        return input;
      });
    }

    pairError() {
      if (!this.config.pair || !this.engraving()) return '';
      if (!this.logoOn() && !this.lineValues.some((line) => line.trim())) return 'Please add wording for Glass 1 (or both glasses).';
      if (!this.logoOn() && this.pairMode === 'different' && !this.secondLineValues.some((line) => line.trim())) return 'Please add wording for Glass 2, or choose the same engraving on both.';
      const groups = [this.lineValues, ...(this.pairMode === 'different' ? [this.secondLineValues] : [])];
      if (groups.some((lines) => lines.some((line) => ['Both glasses:', 'Glass 1:', 'Glass 2:'].includes(line.trim())))) return 'Please use your own wording rather than the Glass 1 / Glass 2 instruction headings.';
      return '';
    }

    // Where the words go and how many fit, shown under the preview
    placementNote() {
      if (this.space.note) return this.space.note;
      if (this.isAward) return 'Engraved on the front of the glass';
      if (this.config.glass) return 'On the outside of the bowl · up to 2 short lines';
      if (this.config.placement === 'base') return 'Underneath, on the base · up to 3 short lines';
      if (this.config.kind === 'whisky-glass') return 'Frosted, on the side of the glass · up to 4 short lines';
      return '';
    }

    previewShape() {
      const shape = this.space.shape;
      // Generic: a plain plate of metal or frosted glass, not the shape of any one product
      if (this.config.kind === 'generic') return ` wal-preview__bowl--plate wal-preview__bowl--plate-${this.config.family === 'metal' ? 'metal' : 'glass'}`;
      if (this.isAward) return ' wal-preview__bowl--award';
      if (this.config.glass) return ' wal-preview__bowl--glass';
      if (this.config.kind === 'hip-flask') {
        if (shape === 'round' || shape === 'frame') return ` wal-preview__bowl--flask wal-preview__bowl--flask-round${shape === 'frame' ? ' wal-preview__bowl--flask-frame' : ''}`;
        return ` wal-preview__bowl--flask${shape ? ` wal-preview__bowl--flask-${shape}` : ''}`;
      }
      if (this.config.kind !== 'whisky-glass') return '';
      if (this.config.placement === 'base') return ' wal-preview__bowl--glass-base';
      return ` wal-preview__bowl--tumbler${shape === 'sleeved' ? ' wal-preview__bowl--tumbler-sleeved' : ''}`;
    }

    buildPreview() {
      this.previewText = element('div', { class: 'wal-preview__text', 'aria-hidden': 'true' });
      const bowl = element('div', { class: `wal-preview__bowl${this.previewShape()}` }, [this.previewText]);
      this.preview = element('div', { class: 'wal-preview', 'data-wal-preview': true }, [
        bowl,
        // Phones say it's live, as the lines can push the circle off screen; on desktop it stays in view beside them.
        element('p', { class: 'wal-preview__caption' }, [
          element('span', { class: 'wal-preview__caption--small' }, [element('strong', { text: 'Live preview' }), ' · final layout set by hand']),
          element('span', { class: 'wal-preview__caption--large', text: 'Final layout set by hand' }),
        ]),
        this.placementNote() ? element('p', { class: 'wal-preview__where', text: this.placementNote() }) : '',
      ]);
      // Under the occasions, above the lines, so it's in view while typing.
      this.chips.after(this.preview);
    }

    // "Add your logo or crest" under the words; once chosen, a light panel takes its place: price and Remove, then the file
    buildLogo() {
      const price = this.logoPrice();
      this.logoAdd = element('button', { type: 'button', class: 'wal-logo-add', 'data-wal-logo-add': true, hidden: true }, [
        shieldIcon('wal-logo-add__icon'),
        element('span', { class: 'wal-logo-add__text' }, [
          element('span', { class: 'wal-logo-add__title', text: 'Add your logo or crest' }),
          element('span', { class: 'wal-logo-add__help', text: 'On its own or with your words' }),
          price ? element('span', { class: 'wal-logo-add__price', text: price }) : '',
        ]),
      ]);
      this.logoThumb = element('span', { class: 'wal-logo__thumb', hidden: true });
      this.logoStatus = element('span', { class: 'wal-logo__status', role: 'status' });
      this.logoChoose = element('button', { type: 'button', class: 'wal-logo__choose', 'data-wal-logo-choose': true, text: 'Choose a file' });
      this.logoRemove = element('button', { type: 'button', class: 'wal-logo__remove', 'data-wal-logo-remove': true, text: 'Remove' });
      this.logoHint = element('p', { class: 'wal-logo__hint', role: 'status', hidden: true });
      this.logoProblemText = element('span', { class: 'wal-logo__problem-text' });
      this.logoRetry = element('button', { type: 'button', class: 'wal-logo__retry', 'data-wal-logo-retry': true, hidden: true, text: 'Try again' });
      this.logoProblem = element('p', { class: 'wal-logo__problem', 'data-wal-logo-problem': true, role: 'alert', hidden: true }, [this.logoProblemText, ' ', this.logoRetry]);
      this.logoWidget = element('div', { class: 'wal-logo__widget' });
      this.logoPanel = element('div', { class: 'wal-logo', 'data-wal-logo': true, hidden: true }, [
        element('div', { class: 'wal-logo__head' }, [
          element('p', { class: 'wal-logo__title' }, ['Your logo or crest', price ? element('span', { class: 'wal-logo__price', text: ` ${price}` }) : '']),
          this.logoRemove,
        ]),
        element('div', { class: 'wal-logo__file' }, [this.logoThumb, this.logoChoose, this.logoStatus]),
        // Customers upload photos and screenshots as often as artwork; say what happens to them and that it takes a little time
        element('p', {
          class: 'wal-logo__note',
          'data-wal-logo-note': true,
          hidden: true,
          text: `We'll redraw it as clean engraving artwork, so it won't be engraved as a square photo. ${logoDelay()}`,
        }),
        this.isAward ? element('p', { class: 'wal-logo__note', 'data-wal-logo-proof': true, text: "Your logo goes at the top, above the words. We'll email you a proof before we engrave." }) : '',
        this.logoHint,
        this.logoProblem,
        this.logoWidget,
      ]);
      // After the toast's "no room" note, which belongs next to the toast buttons
      (this.config.pair ? this.secondGroup : this.toastNote).after(this.logoAdd, this.logoPanel);

      this.logoAdd.addEventListener('click', () => this.setLogo(true));
      this.logoChoose.addEventListener('click', () => window.WalArtwork.pick());
      this.logoRetry.addEventListener('click', () => window.WalArtwork.retry());
      window.WalArtwork.attachWidget(this.logoWidget);
      this.logoRemove.addEventListener('click', () => {
        this.setLogo(false);
        this.update();
        this.onChange();
      });
    }

    logoInputs() {
      return this.fields.logo ? [...this.fields.logo.querySelectorAll('input[type="radio"]')] : [];
    }

    logoOn() {
      const chosen = this.checked('logo');
      return Boolean(chosen && chosen.value !== 'No');
    }

    // "+£20 per order" from the option's label "Yes (+£20 per order)" (display only; the cart is priced by the add-on products)
    logoPrice() {
      const yes = this.logoInputs().find((input) => input.value !== 'No');
      const match = yes && yes.value.match(/\+\s*£\s*[\d.,]+(\s*per order)?/i);
      return match ? match[0].replace(/\s+/g, ' ').replace(/\+\s*£\s*/, '+£') : '';
    }

    // 'none', 'uploading', 'done' or 'error', from the shared uploader (artwork-upload.js), which also keeps the link in the product form
    uploadState() {
      return window.WalArtwork.state.status;
    }

    uploadedName() {
      return window.WalArtwork.state.name;
    }

    setLogo(on) {
      const target = this.logoInputs().find((input) => (on ? input.value !== 'No' : input.value === 'No'));
      if (!target || target.checked) return;
      target.click();
      // Otherwise the removed file would still reach the order (no charge, but the engraver would see it)
      if (!on) window.WalArtwork.reset();
      this.hideError();
      this.onTrack('engraving_logo', { action: on ? 'added' : 'removed' });
    }

    // Follows the uploader while this form is showing: the panel, the preview and the saved draft
    bindArtwork() {
      const artwork = window.WalArtwork;
      artwork.claim('custom');
      this.unbindArtwork = artwork.subscribe((current, event) => {
        if (event === 'active') return;
        if (event === 'progress') return this.renderLogoPanel();
        if (event === 'chosen') this.onTrack('engraving_logo', { action: 'file_chosen', type: current.type });
        if (['chosen', 'rejected', 'removed', 'restored'].includes(event)) this.hideError();
        this.update();
        if (['done', 'removed'].includes(event)) this.onChange();
      });
    }

    renderLogoPanel() {
      const artwork = window.WalArtwork.state;
      const state = artwork.status;
      const name = artwork.name;
      const status = {
        none: 'Any image or PDF · we redraw it for engraving',
        uploading: `Uploading ${name}… ${artwork.progress}%`,
        done: `\u2713 ${name || 'Logo'}`,
        error: name || 'No file',
      }[state];
      setText(this.logoStatus, status);
      this.logoPanel.dataset.state = state;
      setText(this.logoChoose, state === 'none' ? 'Choose a file' : 'Change');
      const src = artwork.preview;
      this.logoThumb.hidden = state === 'none' || (state === 'error' && !src);
      this.logoPanel.querySelector('[data-wal-logo-note]').hidden = state === 'none' || state === 'error';
      const current = this.logoThumb.firstElementChild;
      if (src && (!current || current.getAttribute('src') !== src)) this.logoThumb.replaceChildren(element('img', { src, alt: '' }));
      else if (!src && (!current || current.tagName === 'IMG')) this.logoThumb.replaceChildren(shieldIcon('wal-logo__shield'));
      this.logoHint.hidden = !artwork.hint;
      setText(this.logoHint, artwork.hint);
      const problem = state === 'error' && artwork.error ? artwork.error.message : '';
      this.logoProblem.hidden = !problem;
      this.logoRetry.hidden = !(problem && artwork.error.retry);
      if (this.logoProblemText.dataset.message !== problem) {
        this.logoProblemText.dataset.message = problem;
        this.logoProblemText.replaceChildren(...(problem ? window.WalArtwork.messageNodes(problem) : []));
      }
    }

    logoError() {
      return {
        uploading: 'Your logo is still uploading. Give it a moment, then add to cart again.',
        error: "Your logo didn't upload. Try again or choose another file, or remove the logo.",
      }[this.uploadState()] || 'Please choose your logo file, or remove the logo.';
    }

    buildExtrasToggle() {
      if (!this.fields.notes) return;
      this.extrasToggle = element('button', {
        type: 'button',
        class: 'wal-extras-toggle',
        'data-wal-extras-toggle': true,
        id: 'wal-extras-toggle',
        'aria-expanded': 'false',
        'aria-controls': this.fields.notes.id,
        text: 'Add a note for the engraver',
      });
      const notes = this.noteInput();
      if (notes) notes.setAttribute('aria-labelledby', 'wal-extras-toggle');
      this.extrasToggle.addEventListener('click', () => {
        // A note that has words stays open: closing it would hide words that still go to the engraver
        if (this.extrasOpen && notes && notes.value.trim()) return;
        this.setExtrasOpen(!this.extrasOpen);
        if (this.extrasOpen && notes) notes.focus();
      });
      this.fields.notes.before(this.extrasToggle);
    }

    noteInput() {
      return this.fields.notes && this.fields.notes.querySelector('input, textarea');
    }

    setExtrasOpen(open) {
      this.extrasOpen = open;
      this.container.classList.toggle('wal-extras-open', open);
      if (this.extrasToggle) this.extrasToggle.setAttribute('aria-expanded', String(open));
      // The box grows with its words, which can only be measured once it is showing
      if (open && window.WalEngraving.fitNote) window.WalEngraving.fitNote();
    }

    // What this piece costs with the engraving, in the same itemised box Several awards uses. The logo set-up is not in the total: the cart
    // charges it once per order on its own line, and the page price and the Add to cart button leave it out too.
    buildPriceSummary() {
      this.priceSummary = element('div', { class: 'wal-summary wal-summary--single', 'data-wal-price-summary': true, hidden: true });
      this.container.append(this.priceSummary);
    }

    renderPriceSummary() {
      const options = window.WalEngraving;
      const base = options.basePrice();
      // Glass awards on desktop already have the price in the panel beside the form (AwardBatch.syncStage)
      const staged = Boolean(this.batch && this.batch.stage && this.batch.stage.isConnected);
      const show = this.engraving() && !this.batchActive() && !staged && base !== null;
      this.priceSummary.hidden = !show;
      if (!show) return;
      const money = options.formatMoney;
      const quantity = options.quantity();
      const added = options.addons();
      const engraving = (added.find((addon) => addon.key === 'engraving') || { price: 0 }).price;
      const infill = added.find((addon) => addon.key === 'infill');
      const noun = this.config.pair ? 'pair' : this.config.noun || this.kind.noun;
      const row = (label, amount, help, extraClass = '') =>
        element('div', { class: `wal-summary__row${extraClass}` }, [
          element('span', { class: 'wal-summary__label' }, [label, help ? element('span', { class: 'wal-summary__help', text: help }) : '']),
          element('span', { class: 'wal-summary__amount', text: amount }),
        ]);
      const unit = base + engraving;
      const parts = `${money(base)} ${noun} + ${money(engraving)} engraving`;
      const nodes = [];
      // One piece and no colour is just the total, with what it is made of; anything more is itemised first
      if (quantity === 1 && !infill) {
        nodes.push(row('Total', money(options.total()), parts, ' wal-summary__row--total wal-summary__row--only'));
      } else {
        nodes.push(row(quantity > 1 ? `${quantity} × ${money(unit)}` : `Engraved ${noun}`, money(unit * quantity), quantity > 1 ? `${parts}, each` : parts));
        if (infill) nodes.push(row(`${this.colourName()} lettering${quantity > 1 ? ` × ${quantity}` : ''}`, money(infill.price * quantity), quantity > 1 ? `${money(infill.price)} each` : ''));
        nodes.push(row('Total', money(options.total() * quantity), '', ' wal-summary__row--total'));
      }
      if (options.logoOn() && options.config.logo && options.config.logo.price) nodes.push(element('p', { class: 'wal-summary__logo', text: `Plus ${money(options.config.logo.price)} logo set-up, once per order` }));
      this.priceSummary.replaceChildren(...nodes);
    }

    // "Antique Gold" from the chosen infill's value ("Antique Gold (+£10)")
    colourName() {
      const chosen = this.checked('infill');
      return chosen ? chosen.value.replace(/\s*\(.*$/, '') : '';
    }

    readTextarea() {
      if (this.config.pair) {
        const state = window.WalWhiskyPair.parse(this.textarea.value);
        this.pairMode = state.mode;
        this.lineValues = state.first[0] === LOGO_ONLY ? [] : state.first.slice(0, this.maxLines);
        this.secondLineValues = state.second[0] === LOGO_ONLY ? [] : state.second.slice(0, this.maxLines);
        this.renderSecond();
        return;
      }
      const value = this.textarea.value === LOGO_ONLY ? '' : this.textarea.value;
      const lines = value.replace(/\r\n/g, '\n').split('\n');
      const kept = lines.slice(0, this.maxLines - 1);
      if (lines.length >= this.maxLines) kept.push(lines.slice(this.maxLines - 1).join(' '));
      this.lineValues = kept;
    }

    renderLines() {
      const occasion = this.occasionLines();
      while (this.lineValues.length < this.baseLines) this.lineValues.push('');
      this.linesList.replaceChildren();
      this.lineInputs = this.lineValues.map((value, index) => {
        const [label, placeholder] = occasion[index] || [`Line ${index + 1}`, ''];
        const id = `wal-line-${index + 1}`;
        const input = element('input', {
          id,
          type: 'text',
          class: 'wal-line__input',
          'data-wal-line': true,
          maxlength: MAX_LINE_LENGTH,
          autocomplete: 'off',
          autocapitalize: 'words',
          enterkeyhint: index < this.maxLines - 1 ? 'next' : 'done',
          placeholder,
        });
        input.value = value;
        const count = element('span', { class: 'wal-line__count', 'aria-hidden': 'true' });
        input.addEventListener('input', () => {
          this.lineValues[index] = input.value;
          this.hideError();
          this.writeTextarea();
          this.update();
          this.onChange();
        });
        input.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          if (index + 1 < this.lineInputs.length) this.lineInputs[index + 1].focus();
          else if (this.lineValues.length < this.maxLines) this.addLineButton.click();
        });
        const labelRow = element('div', { class: 'wal-line__label' }, [element('label', { for: id, text: label }), count]);
        if (index >= this.baseLines) {
          const remove = element('button', { type: 'button', class: 'wal-line__remove', 'data-wal-remove-line': true, 'aria-label': `Remove line ${index + 1}`, text: 'Remove' });
          remove.addEventListener('click', () => this.removeLine(index));
          labelRow.append(remove);
        }
        this.linesList.append(element('div', { class: 'wal-line' }, [labelRow, input]));
        return input;
      });
      this.occasionButtons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.walOccasion === this.occasion)));
    }

    // The occasion's labelled lines, trimmed to what fits this quaich (glass quaichs have their own pair where needed).
    occasionLines() {
      const occasion = this.kind.occasions.find((item) => item.id === this.occasion) || this.kind.occasions[0];
      return ((this.config.glass && occasion.glassLines) || occasion.lines).slice(0, this.maxLines);
    }

    // Adds the toast on the occasion's optional extra line, else the first free line after the words so far (a new line if needed);
    // corrects a misspelt one, or takes it off again without leaving a gap.
    toggleToast(phrase) {
      const existing = this.lineValues.findIndex((value) => plainLetters(value) === plainLetters(phrase));
      this.toastNote.hidden = true;
      this.onTrack('engraving_toast', { phrase, action: existing >= 0 && this.lineValues[existing].trim() === phrase ? 'removed' : 'added' });
      if (existing >= 0 && this.lineValues[existing].trim() === phrase) {
        this.lineValues.splice(existing, 1);
      } else if (existing >= 0) {
        this.lineValues[existing] = phrase;
      } else {
        const extra = this.occasionLines().findIndex(([label]) => label.startsWith('Extra line'));
        const lastFilled = this.lineValues.reduce((last, value, index) => (value.trim() ? index : last), -1);
        const free = extra > lastFilled && !(this.lineValues[extra] || '').trim()
          ? extra
          : this.lineValues.findIndex((value, index) => index > lastFilled && !value.trim());
        if (free >= 0) this.lineValues[free] = phrase;
        else if (this.lineValues.length < this.maxLines) this.lineValues.push(phrase);
        else {
          this.toastNote.hidden = false;
          return;
        }
      }
      this.hideError();
      this.renderLines();
      this.writeTextarea();
      this.update();
      this.onChange();
    }

    removeLine(index) {
      this.lineValues.splice(index, 1);
      this.renderLines();
      this.writeTextarea();
      this.update();
      this.onChange();
      this.lineInputs[index - 1].focus();
    }

    writeTextarea() {
      const lines = [...this.lineValues];
      while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
      const text = this.batchActive()
        ? this.batch.firstText()
        : this.config.pair
        ? window.WalWhiskyPair.format({ mode: this.pairMode, first: lines, second: this.secondLineValues }, this.logoOn())
        : lines.join('\n') || (this.logoOn() ? LOGO_ONLY : '');
      if (this.textarea.value === text) return;
      this.textarea.value = text;
      dispatchInputEvents(this.textarea);
    }

    engraving() {
      const checked = this.engravingInputs.find((input) => input.checked);
      return Boolean(checked && checked.value !== 'No');
    }

    // "Several awards" chosen and engraving on: the words come from the batch table and each award becomes its own cart line
    batchActive() {
      return Boolean(this.batch && this.batch.mode === 'batch' && this.engraving());
    }

    // Line index -> the label its box has for this occasion (extra lines are "Line N")
    lineLabel(index) {
      return (this.occasionLines()[index] || [`Line ${index + 1}`])[0];
    }

    update() {
      this.container.classList.toggle('wal-engrave-on', this.engraving());
      this.syncReady();
      this.applyDefaultFont();
      this.lineInputs.forEach((input) => {
        const count = input.parentElement.querySelector('.wal-line__count');
        count.textContent = input.value.length ? String(input.value.length) : '';
        count.classList.toggle('wal-line__count--long', input.value.length > LONG_LINE);
      });
      this.longNote.hidden = !this.lineValues.some((line) => line.length > LONG_LINE);
      this.renderSamples();
      this.renderPriceSummary();
      this.addLineButton.hidden = this.lineValues.length >= this.maxLines;
      this.toastButtons.forEach((button) => {
        const on = this.lineValues.some((value) => value.trim() === button.dataset.walToast);
        if (button.getAttribute('aria-pressed') !== String(on)) button.setAttribute('aria-pressed', String(on));
      });

      const notes = this.noteInput();
      if (notes && notes.value.trim() && !this.extrasOpen) this.setExtrasOpen(true);

      const logoOn = this.logoOn();
      this.logoAdd.hidden = !this.fields.logo || logoOn;
      this.logoPanel.hidden = !logoOn;
      window.WalArtwork.setActive(logoOn && this.engraving());
      if (logoOn) this.renderLogoPanel();
      if (this.batch) this.batch.sync();
      this.writeTextarea();

      if (this.engraving()) this.renderPreview();
    }

    renderPreview() {
      let lines;
      let flags;
      let example;
      if (this.batchActive()) {
        ({ lines, flags, example } = this.batch.previewView());
      } else {
        const typed = [...this.lineValues];
        while (typed.length && !typed[typed.length - 1].trim()) typed.pop();
        const shown = this.occasionLines().filter(([, placeholder]) => placeholder);
        lines = typed.length ? typed : this.config.pair && this.logoOn() ? [] : shown.map(([, placeholder]) => placeholder);
        const larger = this.batch && this.batch.nameLarger;
        flags = lines.map((_, index) => Boolean(larger && isNameLabel(typed.length ? this.lineLabel(index) : shown[index][0])));
        example = !typed.length;
      }
      this.previewText.classList.toggle('wal-preview__text--example', example);

      // Until the popular default is applied, preview it rather than the option set's first font, so only one web font downloads.
      const font = fontDefaultDone ? this.checked('font') : this.popularFont() || this.checked('font');
      const style = previewFont(font && font.value);
      Object.assign(this.previewText.style, style);
      // Infill colours the words on glass awards; elsewhere there is no infill, so this stays as it was
      this.previewText.style.color = (this.batchActive() ? this.batch.previewColour() : this.infillColour()) || '';
      const family = style.fontFamily;
      const nodes = lines.map((line, index) => element('div', { text: line || '\u00a0', class: flags[index] ? 'wal-preview__name' : false }));
      // The logo above the words, sized in em so it scales with them; a crest outline until there's an image to show
      if (this.logoOn()) {
        const src = this.logoFile && this.logoFile.url;
        const logoClass = `wal-preview__logo${this.logoFile && this.logoFile.photo ? ' wal-preview__logo--photo' : ''}`;
        const logo = src ? element('img', { class: logoClass, src, alt: '' }) : shieldIcon('wal-preview__logo wal-preview__logo--placeholder');
        if (src) logo.addEventListener('load', () => this.fitPreview(), { once: true });
        nodes.unshift(logo);
      }
      this.previewText.replaceChildren(...nodes);
      if (this.config.pair) {
        this.secondPreviewText.style.cssText = this.previewText.style.cssText;
        const second = this.pairMode === 'same' ? lines : this.secondLineValues.filter((line) => line.trim());
        const copies = second.map((line) => element('div', { text: line }));
        if (this.logoOn()) copies.unshift(nodes[0].cloneNode(true));
        this.secondPreviewText.replaceChildren(...copies);
      }
      this.fitPreview();
      if (document.fonts && document.fonts.load) {
        document.fonts.load(`${style.fontWeight} 24px ${family.split(',')[0]}`).then(() => this.fitPreview(), () => {});
      }
    }

    // Measuring forces layout, so wait until after the next paint and coalesce repeated calls.
    fitPreview() {
      if (this.fitPending) return;
      this.fitPending = true;
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.fitPending = false;
          if (this.previewText.isConnected) this.measurePreview();
        }, 0);
      });
    }

    measurePreview() {
      for (const box of [this.previewText, this.secondPreviewText].filter(Boolean)) {
        box.style.fontSize = '30px';
        const width = box.clientWidth, height = box.clientHeight;
        if (!width || !height) continue;
        const widest = Math.max(1, ...[...box.children].map((line) => line.offsetWidth));
        const scale = Math.min(1, width / widest, height / Math.max(1, box.scrollHeight));
        let size = Math.max(9, Math.floor(30 * scale));
        box.style.fontSize = `${size}px`;
        // Wrapped lines (award wording) and a logo above them don't shrink in step with the font, so step down until it fits
        while (size > 9 && box.scrollHeight > height + 1) {
          size -= 1;
          box.style.fontSize = `${size}px`;
        }
      }
    }

    showError(message, field) {
      const target = field || this.fields.text;
      this.error.textContent = message.replace(/personalized/gi, 'personalised');
      this.error.hidden = false;
      if (target === this.fields.text) this.root.append(this.error);
      else target.after(this.error);
      if (target.classList.contains('wal-extra')) this.setExtrasOpen(true);

      const empty = this.lineInputs.find((input) => !input.value.trim());
      const focusTarget = target === this.fields.text ? empty || this.lineInputs[0] : target.querySelector('input, textarea, select');
      (focusTarget || target).scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
      if (focusTarget) {
        // The box the message is about is marked and points at it, for screen readers and for the red outline
        this.hideError();
        this.error.hidden = false;
        focusTarget.setAttribute('aria-invalid', 'true');
        focusTarget.setAttribute('aria-describedby', 'wal-error');
        focusTarget.focus({ preventScroll: true });
      }
    }

    hideError() {
      this.error.hidden = true;
      this.container.querySelectorAll('[aria-describedby="wal-error"]').forEach((node) => {
        node.removeAttribute('aria-invalid');
        node.removeAttribute('aria-describedby');
      });
    }

    // True while the form is attached to the page's inputs.
    intact() {
      return this.root.isConnected && this.textarea.isConnected && this.container.contains(this.textarea);
    }

    syncReady() {
      const ready = this.engraving();
      this.container.classList.toggle('wal-ready', ready);
      this.preview.hidden = !ready;
      if (ready && this.previewText.isConnected) this.fitPreview();
    }

    teardown() {
      [this.textarea, ...this.logoInputs()].forEach((input) => {
        input.removeAttribute('tabindex');
        input.removeAttribute('aria-hidden');
      });
      if (this.lettersWatch) this.lettersWatch.disconnect();
      if (this.unbindArtwork) this.unbindArtwork();
      window.WalArtwork.release('custom');
      if (this.batch) this.batch.teardown();
      [this.root, this.preview, this.extrasToggle, this.error].forEach((node) => node && node.remove());
      this.container.classList.remove('wal-io', 'wal-engrave-on', 'wal-extras-open', 'wal-ready');
    }
  }

  // Glass awards, "Several awards" mode: the lines that are the same on every award, plus a table of what differs on each
  // (award title, name, quantity). The words go into the Text box for award 1; on Add to cart the lines provider in start()
  // (called by engraving-options.js) turns the form into one cart line per award, each with its add-ons. Parsing, composing, checking, pricing and the cart
  // items are in award-batch-text.js.
  const BATCH_ROWS = 3;
  const NAME_LARGER = 'Name larger than the rest';
  const BATCH_EXAMPLES = [
    ['Player of the Year', 'Jamie Wilson'],
    ['Top Scorer', 'Alex Brown'],
    ['Coach of the Year', 'Sam Reid'],
    ['Most Improved', 'Ella Grant'],
    ['Best Newcomer', 'Ruby Hay'],
    ['Golden Boot', 'Noah Campbell'],
    ['Club Person of the Year', 'Isla Murray'],
    ["Players' Player", 'Callum Reid'],
  ];
  const jsonResponse = (status, body) => new Response(JSON.stringify(body), { status, headers: { 'Content-Type': 'application/json' } });
  const WORKSHOP_EMAIL = 'info@waltersofclydebank.co.uk';
  const isoToday = () => {
    const now = new Date();
    return `${now.getFullYear()}-${twoDigits(now.getMonth() + 1)}-${twoDigits(now.getDate())}`;
  };

  class AwardBatch {
    constructor(form) {
      this.form = form;
      this.api = window.WalAwardBatch;
      this.max = form.config.batchMax || 60;
      this.maxLines = form.maxLines;
      this.mode = 'one';
      // The chosen colour goes on every award, or only on the rows ticked in the list (e.g. just the President's award)
      this.colourAll = true;
      this.nameLarger = false;
      this.neededBy = '';
      this.current = 0;
      this.keys = 0;
      this.shared = [
        this.textLine('Organisation or event', 'Clydebank Swimming Club'),
        { type: 'award', key: this.nextKey() },
        { type: 'name', key: this.nextKey() },
        this.textLine('Year or date', String(year)),
      ];
      this.rows = Array.from({ length: BATCH_ROWS }, () => this.blankRow());
    }

    nextKey() {
      this.keys += 1;
      return this.keys;
    }

    textLine(label, placeholder, value = '') {
      return { type: 'text', label, placeholder, value, key: this.nextKey() };
    }

    blankRow() {
      return { award: '', name: '', tbc: false, qty: 1, colour: false };
    }

    build() {
      this.buildModes();
      this.buildNameLarger();
      this.buildShared();
      this.buildTable();
      this.buildNav();
      const { root, addLineButton, logoPanel } = this.form;
      root.querySelector('.wal-engrave__heading').after(this.modes);
      addLineButton.after(this.sharedBlock, this.nameLargerLabel);
      logoPanel.after(this.tableBlock);
      this.buildColourScope();
      this.form.container.append(this.colourScope, this.checkoutBlock);
      this.buildStage();
      this.renderShared();
      this.renderRows();
    }

    // The mode cards are real radios inside labels, so keyboard, screen readers and taps all work as a radio group
    buildModes() {
      this.modeInputs = [];
      const card = (mode, title, help) => {
        const input = element('input', { type: 'radio', name: 'wal-award-mode', value: mode });
        input.addEventListener('change', () => input.checked && this.setMode(mode, true));
        this.modeInputs.push(input);
        return element('label', { class: 'wal-mode__card', 'data-wal-mode': mode }, [
          input,
          element('span', { class: 'wal-mode__title', text: title }),
          element('span', { class: 'wal-mode__help', text: help }),
        ]);
      };
      this.modes = element('fieldset', { class: 'wal-mode' }, [
        element('legend', { class: 'visually-hidden', text: 'How many awards are you engraving?' }),
        card('one', 'One award', 'Or several with the same words (use Quantity)'),
        card('batch', 'Several awards', 'Different names or titles on each — a team, club or company awards night'),
      ]);
    }

    setMode(mode, byCustomer = false) {
      if (this.mode === mode) return;
      this.mode = mode;
      if (byCustomer) this.form.onTrack('engraving_batch', { action: mode === 'batch' ? 'on' : 'off', ...this.counts() });
      this.form.update();
      this.form.onChange();
    }

    buildNameLarger() {
      this.nameLargerBox = element('input', { type: 'checkbox', 'data-wal-name-large': true });
      this.nameLargerBox.addEventListener('change', () => {
        this.nameLarger = this.nameLargerBox.checked;
        this.form.update();
        this.form.onChange();
      });
      this.nameLargerLabel = element('label', { class: 'wal-check' }, [
        this.nameLargerBox,
        element('span', { class: 'wal-check__text' }, ['Make the name stand out', element('span', { class: 'wal-check__help', text: 'Engraved larger than the other lines' })]),
      ]);
    }

    // --- The same on every award ---

    buildShared() {
      this.sharedList = element('ol', { class: 'wal-shared', 'aria-label': 'Lines on every award' });
      this.sharedAdd = element('button', { type: 'button', class: 'wal-add-line link', 'data-wal-shared-add': true, text: '+ Add a line' });
      this.sharedFull = element('p', { class: 'wal-note', hidden: true, text: `Up to ${this.maxLines} lines fit on an award, counting the title and the name.` });
      this.sharedStatus = element('p', { class: 'visually-hidden', role: 'status' });
      this.sharedAdd.addEventListener('click', () => this.addShared());
      this.sharedBlock = element('div', { class: 'wal-batch-shared', 'data-wal-batch-shared': true, hidden: true }, [
        element('h3', { class: 'wal-batch__heading', text: 'The same on every award' }),
        element('p', { class: 'wal-note', text: 'Use the arrows to put the award title and the winner\'s name where you want them.' }),
        this.sharedList,
        this.sharedAdd,
        this.sharedFull,
        this.sharedStatus,
      ]);
    }

    slotName(item) {
      return item.type === 'award' ? 'Award title' : item.type === 'name' ? "Winner's name" : item.label;
    }

    renderShared(focus) {
      const last = this.shared.length - 1;
      const rows = this.shared.map((item, index) => {
        const name = this.slotName(item);
        const up = element('button', { type: 'button', class: 'wal-move', 'data-wal-shared-up': true, 'aria-label': `Move ${name} up`, text: '↑' });
        const down = element('button', { type: 'button', class: 'wal-move', 'data-wal-shared-down': true, 'aria-label': `Move ${name} down`, text: '↓' });
        up.disabled = index === 0;
        down.disabled = index === last;
        up.addEventListener('click', () => this.moveShared(index, -1));
        down.addEventListener('click', () => this.moveShared(index, 1));
        let body;
        if (item.type === 'text') {
          const id = `wal-shared-${item.key}`;
          const input = element('input', {
            id,
            type: 'text',
            class: 'wal-line__input',
            'data-wal-shared-input': true,
            maxlength: MAX_LINE_LENGTH,
            autocomplete: 'off',
            autocapitalize: 'words',
            placeholder: item.placeholder,
          });
          input.value = item.value;
          input.addEventListener('input', () => {
            item.value = input.value;
            this.edited();
          });
          input.addEventListener('keydown', (event) => event.key === 'Enter' && event.preventDefault());
          const remove = element('button', { type: 'button', class: 'wal-line__remove', 'data-wal-shared-remove': true, 'aria-label': `Remove ${item.label}`, text: 'Remove' });
          remove.addEventListener('click', () => this.removeShared(index));
          body = [element('div', { class: 'wal-line__label' }, [element('label', { for: id, text: item.label }), remove]), input];
        } else {
          body = [element('p', { class: 'wal-slot' }, [element('strong', { text: name }), ' · different on each award'])];
        }
        return element('li', { class: `wal-shared__row wal-shared__row--${item.type}`, 'data-wal-shared-line': item.type, 'data-wal-key': item.key }, [
          element('div', { class: 'wal-shared__body' }, body),
          element('div', { class: 'wal-shared__move' }, [up, down]),
        ]);
      });
      this.sharedList.replaceChildren(...rows);
      this.sharedAdd.hidden = this.shared.length >= this.maxLines;
      this.sharedFull.hidden = this.shared.length < this.maxLines;
      if (focus) {
        const row = this.sharedList.querySelector(`[data-wal-key="${focus.key}"]`);
        const buttons = row ? [...row.querySelectorAll(focus.selector)] : [];
        const target = buttons.find((button) => !button.disabled) || (row && row.querySelector('.wal-move:not(:disabled)'));
        if (target) target.focus();
      }
    }

    moveShared(index, delta) {
      const target = index + delta;
      if (target < 0 || target >= this.shared.length) return;
      const [item] = this.shared.splice(index, 1);
      this.shared.splice(target, 0, item);
      this.renderShared({ key: item.key, selector: delta < 0 ? '[data-wal-shared-up]' : '[data-wal-shared-down]' });
      this.sharedStatus.textContent = `${this.slotName(item)} moved ${delta < 0 ? 'up' : 'down'}, now line ${target + 1} of ${this.shared.length}`;
      this.edited();
    }

    removeShared(index) {
      this.shared.splice(index, 1);
      this.renderShared();
      const inputs = this.sharedList.querySelectorAll('[data-wal-shared-input]');
      (inputs[Math.max(0, Math.min(index - 1, inputs.length - 1))] || this.sharedAdd).focus();
      this.edited();
    }

    addShared() {
      if (this.shared.length >= this.maxLines) return;
      this.shared.push(this.textLine('Extra line', ''));
      this.renderShared();
      const inputs = this.sharedList.querySelectorAll('[data-wal-shared-input]');
      inputs[inputs.length - 1].focus();
      this.edited();
    }

    // --- Different on each award ---

    buildTable() {
      this.rowsHead = element('div', { class: 'wal-brows__head', 'aria-hidden': 'true' }, [
        element('span', { text: '#' }),
        element('span', { text: 'Award title' }),
        element('span', { text: 'Name' }),
        element('span', { text: 'Qty' }),
        element('span'),
      ]);
      this.rowsList = element('div', { class: 'wal-brows', 'data-wal-batch-rows': true });
      this.tbcHelp = element('p', { class: 'wal-note', 'data-wal-batch-tbc-help': true, hidden: true, text: `Email the winners' names to ${WORKSHOP_EMAIL} with your order number at least 5 working days before you need the awards.` });
      this.addRowButton = element('button', { type: 'button', class: 'wal-add-line link', 'data-wal-batch-add': true, text: '+ Add an award' });
      this.pasteButton = element('button', { type: 'button', class: 'wal-add-line link', 'data-wal-batch-paste': true, 'aria-expanded': 'false', 'aria-controls': 'wal-batch-paste', text: 'Paste your list' });
      this.pasteText = element('textarea', { id: 'wal-batch-paste-text', class: 'wal-line__input wal-paste__text', 'data-wal-batch-paste-text': true, rows: '6', autocomplete: 'off' });
      this.pasteApply = element('button', { type: 'button', class: 'button button--secondary wal-paste__apply', 'data-wal-batch-paste-apply': true, text: 'Add these awards' });
      this.pasteCancel = element('button', { type: 'button', class: 'wal-add-line link', text: 'Cancel' });
      this.pastePanel = element('div', { id: 'wal-batch-paste', class: 'wal-paste', hidden: true }, [
        element('label', { for: 'wal-batch-paste-text', text: 'Paste from a spreadsheet, Word or an email — one award per line' }),
        element('p', { class: 'wal-note', text: 'Award first, then the name: "Player of the Year - Jamie Wilson". Add x2 at the end for two of the same.' }),
        this.pasteText,
        element('div', { class: 'wal-paste__actions' }, [this.pasteApply, this.pasteCancel]),
      ]);
      this.pasteStatus = element('p', { class: 'wal-note', role: 'status', hidden: true });
      this.sizeNote = element('p', { class: 'wal-note', 'data-wal-batch-size': true, hidden: true });
      this.neededInput = element('input', { id: 'wal-batch-needed', type: 'date', class: 'wal-line__input', 'data-wal-batch-needed': true, min: isoToday() });
      this.summary = element('div', { class: 'wal-summary', 'data-wal-batch-total': true });
      this.error = element('p', { class: 'wal-error', role: 'alert', 'data-wal-batch-error': true, hidden: true });

      this.addRowButton.addEventListener('click', () => this.addRow());
      this.pasteButton.addEventListener('click', () => this.togglePaste(this.pastePanel.hidden));
      this.pasteCancel.addEventListener('click', () => {
        this.togglePaste(false);
        this.pasteButton.focus();
      });
      this.pasteApply.addEventListener('click', () => this.applyPaste());
      this.neededInput.addEventListener('input', () => {
        this.neededBy = this.neededInput.value;
        this.edited();
      });
      this.neededInput.addEventListener('keydown', (event) => event.key === 'Enter' && event.preventDefault());

      const quote = this.form.config.quoteUrl;
      // Organisers usually have the winners in a spreadsheet or an email already, so pasting is offered before typing
      this.tableBlock = element('div', { class: 'wal-batch-table', 'data-wal-batch-table': true, hidden: true }, [
        element('h3', { class: 'wal-batch__heading', text: 'Different on each award' }),
        element('p', { class: 'wal-note wal-batch__paste-prompt' }, ['Got the list in a spreadsheet, Word or an email? ', this.pasteButton]),
        this.pastePanel,
        this.pasteStatus,
        this.rowsHead,
        this.rowsList,
        this.tbcHelp,
        element('div', { class: 'wal-batch__actions' }, [this.addRowButton]),
      ]);
      // Size, date and total sit last, after lettering and colour, so the total is the final thing read before the button
      this.checkoutBlock = element('div', { class: 'wal-batch-checkout', 'data-wal-batch-checkout': true, hidden: true }, [
        this.sizeNote,
        element('div', { class: 'wal-needed' }, [element('label', { for: 'wal-batch-needed', text: 'When do you need them? (optional)' }), this.neededInput]),
        this.summary,
        element('p', { class: 'wal-note wal-batch__quote' }, [`Over ${this.max} awards, or mixed styles? `, quote ? element('a', { href: quote, text: 'Get a quote' }) : 'Get a quote']),
      ]);
    }

    exampleFor(index) {
      return BATCH_EXAMPLES[index % BATCH_EXAMPLES.length];
    }

    buildRow(row, index) {
      const [awardExample, nameExample] = this.exampleFor(index);
      const id = `wal-batch-${index + 1}`;
      const field = (suffix, label, input) => element('div', { class: `wal-brow__field wal-brow__${suffix}` }, [element('label', { for: input.id, class: 'wal-brow__label', text: label }), input]);
      const textInput = (suffix, attribute, placeholder, value) => {
        const input = element('input', { id: `${id}-${suffix}`, type: 'text', class: 'wal-line__input', [attribute]: true, maxlength: MAX_LINE_LENGTH, autocomplete: 'off', autocapitalize: 'words', enterkeyhint: 'next', placeholder });
        input.value = value;
        return input;
      };
      const award = textInput('award', 'data-wal-batch-award', awardExample, row.award);
      const name = textInput('name', 'data-wal-batch-name', row.tbc ? 'Name to follow' : nameExample, row.tbc ? '' : row.name);
      name.disabled = row.tbc;
      const tbc = element('input', { id: `${id}-tbc`, type: 'checkbox', 'data-wal-batch-tbc': true });
      tbc.checked = row.tbc;
      const qty = element('input', { id: `${id}-qty`, type: 'number', inputmode: 'numeric', class: 'wal-line__input', 'data-wal-batch-qty': true, min: '1', max: String(this.api.MAX_QTY), step: '1' });
      qty.value = String(row.qty);
      const remove = element('button', { type: 'button', class: 'wal-brow__remove', 'data-wal-batch-remove': true, 'aria-label': `Remove award ${index + 1}`, text: '✕' });

      const edit = () => {
        this.current = this.rows.indexOf(row);
        this.edited();
      };
      award.addEventListener('input', () => {
        row.award = award.value;
        edit();
      });
      name.addEventListener('input', () => {
        row.name = name.value;
        edit();
      });
      tbc.addEventListener('change', () => {
        row.tbc = tbc.checked;
        name.disabled = row.tbc;
        name.value = row.tbc ? '' : row.name;
        name.placeholder = row.tbc ? 'Name to follow' : nameExample;
        edit();
      });
      qty.addEventListener('input', () => {
        const count = parseInt(qty.value, 10);
        row.qty = count >= 1 ? Math.min(count, this.api.MAX_QTY) : 1;
        edit();
      });
      qty.addEventListener('change', () => {
        qty.value = String(row.qty);
      });
      [award, name, tbc, qty].forEach((input) => input.addEventListener('focus', () => this.showRow(this.rows.indexOf(row))));
      const next = (target) => {
        const at = this.rows.indexOf(row);
        if (target === 'award') return this.focusRow(at + 1, '[data-wal-batch-award]', true);
        return this.focusRow(at, '[data-wal-batch-name]:not(:disabled)') || this.focusRow(at + 1, '[data-wal-batch-award]', true);
      };
      award.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        next('name');
      });
      [name, qty].forEach((input) =>
        input.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          next('award');
        })
      );
      remove.addEventListener('click', () => this.removeRow(this.rows.indexOf(row)));
      const colour = element('input', { id: `${id}-colour`, type: 'checkbox', 'data-wal-batch-colour': true });
      colour.checked = Boolean(row.colour);
      colour.addEventListener('change', () => {
        row.colour = colour.checked;
        edit();
      });
      colour.addEventListener('focus', () => this.showRow(this.rows.indexOf(row)));
      const colourLabel = element('label', { class: 'wal-brow__colour', for: colour.id, hidden: true }, [colour, element('span', { class: 'wal-swatch', 'aria-hidden': 'true' }), element('span', { class: 'wal-brow__colour-name' })]);

      return element('div', { class: 'wal-brow', 'data-wal-batch-row': true }, [
        element('span', { class: 'wal-brow__num', text: String(index + 1) }),
        element('div', { class: 'wal-brow__field wal-brow__award' }, [element('label', { for: award.id, class: 'wal-brow__label', text: 'Award title' }), award, colourLabel]),
        element('div', { class: 'wal-brow__field wal-brow__name' }, [
          element('label', { for: name.id, class: 'wal-brow__label', text: 'Name' }),
          name,
          element('label', { class: 'wal-tbc', for: tbc.id }, [tbc, 'Not known yet']),
        ]),
        field('qty', 'Qty', qty),
        remove,
      ]);
    }

    renderRows(focus) {
      this.current = Math.min(this.current, this.rows.length - 1);
      this.rowsList.replaceChildren(...this.rows.map((row, index) => this.buildRow(row, index)));
      this.addRowButton.disabled = this.rows.length >= this.max;
      if (focus) this.focusRow(focus.index, focus.selector);
    }

    // Focuses a cell of a row; true when it could. addIfLast adds a row after the last one first.
    focusRow(index, selector, addIfLast = false) {
      if (index >= this.rows.length && addIfLast && !this.addRow()) return false;
      const node = this.rowsList.children[index] && this.rowsList.children[index].querySelector(selector);
      if (!node || node.disabled) return false;
      node.focus();
      return true;
    }

    addRow() {
      if (this.rows.length >= this.max) return false;
      this.rows.push(this.blankRow());
      this.current = this.rows.length - 1;
      this.renderRows({ index: this.rows.length - 1, selector: '[data-wal-batch-award]' });
      this.form.update();
      this.form.onChange();
      return true;
    }

    removeRow(index) {
      if (index < 0) return;
      if (this.rows.length === 1) this.rows[0] = this.blankRow();
      else this.rows.splice(index, 1);
      this.current = Math.min(this.current, this.rows.length - 1);
      this.hideError();
      this.renderRows({ index: Math.min(index, this.rows.length - 1), selector: '[data-wal-batch-remove]' });
      this.form.update();
      this.form.onChange();
    }

    togglePaste(open) {
      this.pastePanel.hidden = !open;
      this.pasteButton.setAttribute('aria-expanded', String(open));
      if (open) this.pasteText.focus();
    }

    setPasteStatus(text) {
      this.pasteStatus.textContent = text;
      this.pasteStatus.hidden = !text;
    }

    // Replaces the empty rows and appends to the filled ones, up to the cap
    applyPaste() {
      const parsed = this.api.parseList(this.pasteText.value);
      if (!parsed.length) {
        this.setPasteStatus("We couldn't find any awards in that. Put one award on each line, award first and then the name.");
        this.pasteText.focus();
        return;
      }
      const kept = this.rows.filter(this.api.isFilled);
      const added = parsed.slice(0, Math.max(this.max - kept.length, 0)).map((row) => ({ award: row.award.slice(0, MAX_LINE_LENGTH), name: row.name.slice(0, MAX_LINE_LENGTH), tbc: row.tbc, qty: row.qty, colour: false }));
      const left = parsed.length - added.length;
      this.rows = kept.concat(added);
      if (this.rows.length < this.max) this.rows.push(this.blankRow());
      this.current = kept.length;
      this.pasteText.value = '';
      this.togglePaste(false);
      this.setPasteStatus(
        left
          ? `Added ${added.length} of ${parsed.length} awards. The other ${left} were left out, as up to ${this.max} awards can be ordered here.`
          : `Added ${added.length} award${added.length === 1 ? '' : 's'}. Please check the names below.`
      );
      this.form.onTrack('engraving_batch', { action: 'paste', awards: added.reduce((sum, row) => sum + row.qty, 0), rows: added.length });
      this.hideError();
      this.renderRows();
      this.form.update();
      this.form.onChange();
    }

    showRow(index) {
      if (index < 0 || index === this.current) return;
      this.current = index;
      this.form.update();
    }

    // --- Colour on every award, or only some ---

    buildColourScope() {
      this.colourInputs = [];
      const choice = (value, title, help) => {
        const input = element('input', { type: 'radio', name: 'wal-colour-scope', value, 'data-wal-colour-scope': value });
        input.addEventListener('change', () => {
          if (!input.checked) return;
          this.colourAll = value === 'all';
          this.hideError();
          this.form.update();
          this.form.onChange();
        });
        this.colourInputs.push(input);
        return element('label', { class: 'wal-colour-scope__choice' }, [input, element('span', {}, [element('strong', { text: title }), help ? element('span', { class: 'wal-colour-scope__help', text: help }) : ''])]);
      };
      this.colourScopeTitle = element('legend', { class: 'wal-colour-scope__title' });
      this.colourScope = element('fieldset', { class: 'wal-colour-scope', 'data-wal-colour-scope-box': true, hidden: true }, [
        this.colourScopeTitle,
        choice('all', 'Every award', ''),
        choice('some', 'Only some awards', 'Tick them in your list above, e.g. just the top award'),
      ]);
    }

    // Shown once a colour is chosen in "Several awards"; the list then gets a tick per award when it's only some
    syncColour() {
      const name = this.colourName();
      const some = this.colourSome();
      this.colourScope.hidden = !name;
      setText(this.colourScopeTitle, name ? `Which awards in ${name}?` : '');
      this.colourInputs.forEach((input) => {
        const on = (input.value === 'all') === this.colourAll;
        if (input.checked !== on) input.checked = on;
      });
      const colour = this.form.infillColour() || 'transparent';
      [...this.rowsList.children].forEach((node, index) => {
        const label = node.querySelector('.wal-brow__colour');
        if (!label) return;
        label.hidden = !some;
        setText(label.querySelector('.wal-brow__colour-name'), name);
        label.querySelector('.wal-swatch').style.background = colour;
        const box = label.querySelector('input');
        if (box.checked !== Boolean(this.rows[index].colour)) box.checked = Boolean(this.rows[index].colour);
      });
    }

    // --- Desktop: a panel in the empty column under the product photo, kept in view while the form scrolls ---

    buildStage() {
      this.desktop = window.matchMedia('(min-width: 990px)');
      this.stageTitle = element('p', { class: 'wal-stage__title' });
      this.stageSummary = element('div', { class: 'wal-summary wal-stage__summary', 'aria-hidden': 'true' });
      this.stageListTitle = element('p', { class: 'wal-stage__list-title' });
      this.stageList = element('ol', { class: 'wal-stage__list' });
      this.stageSlot = element('div', { class: 'wal-stage__preview' });
      this.stage = element('aside', { class: 'wal-stage', 'aria-label': 'Your awards', 'data-wal-stage': true, hidden: true }, [
        this.stageTitle,
        this.stageSlot,
        this.stageSummary,
        this.stageListTitle,
        this.stageList,
      ]);
      this.onDesktopChange = () => {
        this.placeStage();
        this.form.update();
      };
      this.desktop.addEventListener('change', this.onDesktopChange);
      // The panel's price for "One award" follows the Quantity box, which sits outside the options container
      this.onQuantity = (event) => {
        if (event.target.matches && event.target.matches('.wal-engrave-page input[name="quantity"]') && this.form.intact()) this.form.update();
      };
      document.addEventListener('change', this.onQuantity);
      this.placeStage();
    }

    // The preview itself moves (one element, so the words, lettering and logo stay in step); back under the occasions on phones
    placeStage() {
      const media = document.querySelector('.wal-engrave-page .product__media-wrapper');
      const preview = this.form.preview;
      if (this.desktop.matches && media) {
        if (this.stage.parentElement !== media) media.append(this.stage);
        if (preview.parentElement !== this.stageSlot) this.stageSlot.append(preview);
      } else {
        if (preview.parentElement !== this.form.root) this.form.chips.after(preview);
        this.stage.remove();
      }
    }

    syncStage() {
      if (!this.stage.isConnected) return;
      const batch = this.mode === 'batch';
      this.stage.hidden = !this.form.engraving() || this.form.preview.hidden;
      const quantity = document.querySelector('.wal-engrave-page input[name="quantity"]');
      const qty = Math.max(1, parseInt(quantity && quantity.value, 10) || 1);
      setText(this.stageTitle, batch || qty > 1 ? 'Your awards' : 'Your award');
      if (batch) {
        this.stageSummary.replaceChildren(...this.summaryNodes());
      } else {
        this.stageSummary.replaceChildren(...this.summaryNodes([{ award: 'x', name: 'x', qty, tbc: false, colour: true }], true));
      }
      const listed = batch ? this.rows.map((row, index) => ({ row, index })).filter(({ row }) => this.api.isFilled(row)) : [];
      this.stageListTitle.hidden = !listed.length;
      setText(this.stageListTitle, 'Check your list');
      this.stageList.hidden = !listed.length;
      const colour = this.form.infillColour();
      this.stageList.replaceChildren(
        ...listed.map(({ row, index }) => {
          const coloured = Boolean(colour) && (!this.colourSome() || row.colour);
          const button = element('button', { type: 'button', class: 'wal-stage__item', 'aria-current': index === this.current ? 'true' : false }, [
            element('span', { class: 'wal-stage__num', text: String(index + 1) }),
            element('span', { class: 'wal-stage__words' }, [
              element('span', { class: 'wal-stage__award', text: row.award.trim() || '—' }),
              element('span', { class: `wal-stage__name${row.tbc ? ' wal-stage__name--tbc' : ''}`, text: row.tbc ? 'Name to follow' : row.name.trim() || 'No name yet' }),
            ]),
            coloured ? element('span', { class: 'wal-swatch', 'aria-label': this.colourName(), style: `background: ${colour}` }) : '',
            element('span', { class: 'wal-stage__qty', text: `×${this.api.qtyOf(row)}` }),
          ]);
          button.addEventListener('click', () => this.showRow(index));
          return element('li', {}, [button]);
        })
      );
    }

    // --- Preview ---

    buildNav() {
      this.navLabel = element('span', { class: 'wal-preview__nav-label', role: 'status', 'aria-live': 'polite' });
      this.prevButton = element('button', { type: 'button', class: 'wal-preview__step', 'data-wal-preview-prev': true, 'aria-label': 'Previous award', text: '‹' });
      this.nextButton = element('button', { type: 'button', class: 'wal-preview__step', 'data-wal-preview-next': true, 'aria-label': 'Next award', text: '›' });
      this.prevButton.addEventListener('click', () => this.step(-1));
      this.nextButton.addEventListener('click', () => this.step(1));
      this.nav = element('div', { class: 'wal-preview__nav', hidden: true }, [this.prevButton, this.navLabel, this.nextButton]);
      this.form.preview.querySelector('.wal-preview__bowl').after(this.nav);
    }

    // How many awards the preview steps through: down to the last filled row, but always the one being edited
    navCount() {
      let last = -1;
      this.rows.forEach((row, index) => this.api.isFilled(row) && (last = index));
      return Math.max(last + 1, this.current + 1, 1);
    }

    step(delta) {
      const count = this.navCount();
      this.current = (this.current + delta + count) % count;
      this.form.update();
    }

    previewView() {
      const row = this.rows[this.current] || this.blankRow();
      const lines = [];
      const flags = [];
      const typed = (placeholders) => {
        this.shared.forEach((item) => {
          let text;
          if (item.type === 'text') text = (placeholders ? item.value || item.placeholder : item.value).trim();
          else if (item.type === 'award') text = row.award.trim() || (placeholders ? this.exampleFor(this.current)[0] : '');
          else text = row.tbc ? this.api.NAME_TO_FOLLOW : row.name.trim() || (placeholders ? this.exampleFor(this.current)[1] : '');
          if (!text) return;
          lines.push(text);
          flags.push(Boolean(this.nameLarger && item.type === 'name'));
        });
      };
      typed(false);
      if (lines.length) return { lines, flags, example: false };
      typed(true);
      return { lines, flags, example: true };
    }

    // --- Price, button, errors ---

    counts() {
      const active = this.api.activeRows(this.rows);
      return { awards: active.reduce((sum, row) => sum + this.api.qtyOf(row), 0), rows: active.length };
    }

    selectedVariantId() {
      const input = document.querySelector('.wal-engrave-page product-form input[name="id"]');
      return input ? input.value : '';
    }

    // rows/colourAll default to the list as set; the desktop panel passes one row for "One award" (Quantity, colour on all)
    totals(rows = this.rows, colourAll = !this.colourSome()) {
      const { form, api } = this;
      const variants = form.config.variants || {};
      const base = variants[this.selectedVariantId()];
      const options = window.WalEngraving.config;
      const infill = form.checked('infill');
      const known = Number.isFinite(base);
      return {
        known,
        ...api.totals(rows, {
          base: known ? base : 0,
          engraving: form.engraving() ? options.engraving.price : 0,
          infill: options.infill && infill && infill.value !== options.infill.none ? options.infill.price : 0,
          logo: form.logoOn(),
          logoFee: options.logo ? options.logo.price : 0,
          colourAll,
        }),
      };
    }

    sizeText() {
      const variants = this.form.config.variants || {};
      const title = (this.form.config.variantTitles || {})[this.selectedVariantId()];
      return Object.keys(variants).length > 1 && title ? `All awards in this order are the ${title} size. Need different sizes? Add each size separately.` : '';
    }

    limits() {
      return { maxLines: this.maxLines, maxChars: this.form.textarea.maxLength > 0 ? this.form.textarea.maxLength : 500, maxRows: this.max };
    }

    validateNow() {
      return this.api.validate(this.shared, this.rows, { ...this.limits(), colourSome: this.colourSome() });
    }

    // The chosen colour ("Antique Gold"), or '' for none
    colourName() {
      const chosen = this.form.checked('infill');
      return chosen && chosen.value !== 'No' ? chosen.value.replace(/\s*\(.*$/, '') : '';
    }

    colourSome() {
      return Boolean(this.colourName()) && !this.colourAll;
    }

    previewColour() {
      const row = this.rows[this.current];
      return this.colourSome() && !(row && row.colour) ? null : this.form.infillColour();
    }

    // Award 1's words, for the Text box
    firstText() {
      const first = this.rows.find(this.api.isFilled);
      return first ? this.api.compose(this.shared, first) : '';
    }

    // Before adding: the Text box gets award 1, and the whole list is checked. Returns the first problem or null.
    prepare() {
      this.form.writeTextarea();
      const result = this.validateNow();
      return result.ok ? null : result.errors[0];
    }

    sync() {
      const batch = this.mode === 'batch';
      const active = this.form.batchActive();
      this.modeInputs.forEach((input) => {
        const on = input.value === this.mode;
        if (input.checked !== on) input.checked = on;
      });
      this.form.root.classList.toggle('wal-engrave--batch', batch);
      document.documentElement.classList.toggle('wal-batch-on', active);
      this.sharedBlock.hidden = !batch;
      this.tableBlock.hidden = !batch;
      this.checkoutBlock.hidden = !batch;
      this.nav.hidden = !batch;
      if (this.nameLargerBox.checked !== this.nameLarger) this.nameLargerBox.checked = this.nameLarger;
      this.syncLayoutInput();
      if (batch) {
        [...this.rowsList.children].forEach((node, index) => node.classList.toggle('wal-brow--current', index === this.current));
        const count = this.navCount();
        setText(this.navLabel, `Award ${this.current + 1} of ${count}`);
        this.tbcHelp.hidden = !this.rows.some((row) => row.tbc);
        const size = this.sizeText();
        setText(this.sizeNote, size);
        this.sizeNote.hidden = !size;
        if (this.neededInput.value !== this.neededBy) this.neededInput.value = this.neededBy;
        this.syncColour();
        this.syncSummary();
      } else {
        this.colourScope.hidden = true;
      }
      this.syncStage();
      this.syncButtonLabel(active);
    }

    // Itemised so it's plain what is per award and what is once: "6 awards × £48.00", "Antique Gold on 2 awards × £10.00",
    // "Logo setup, once £20.00", total. Drawn in the form and, on desktop, in the panel beside it.
    summaryNodes(rows = this.rows, colourAll = !this.colourSome()) {
      const totals = this.totals(rows, colourAll);
      const money = (pence) => this.api.formatMoney(pence);
      const plural = (count) => `${count} award${count === 1 ? '' : 's'}`;
      if (!totals.awards) return [element('p', { class: 'wal-summary__empty', text: 'Add your awards to see the price.' })];
      if (!totals.known) return [element('p', { class: 'wal-summary__empty', text: plural(totals.awards) })];
      const row = (label, amount, help, extraClass = '') =>
        element('div', { class: `wal-summary__row${extraClass}` }, [
          element('span', { class: 'wal-summary__label' }, [label, help ? element('span', { class: 'wal-summary__help', text: help }) : '']),
          element('span', { class: 'wal-summary__amount', text: amount }),
        ]);
      const nodes = [row(`${plural(totals.awards)} × ${money(totals.unit)}`, money(totals.awards * totals.unit), `${money(totals.base)} award + ${money(totals.engraving)} engraving, each`)];
      if (totals.coloured) {
        const all = totals.coloured === totals.awards;
        nodes.push(row(`${this.colourName()} lettering × ${totals.coloured}`, money(totals.colour), all ? `${money(totals.infill)} each, on every award` : `${money(totals.infill)} each, on the ticked awards only`));
      }
      if (totals.logo) nodes.push(row('Logo setup', money(totals.logo), 'Once for the whole order'));
      nodes.push(row('Total', money(totals.total), '', ' wal-summary__row--total'));
      return nodes;
    }

    syncSummary() {
      this.summary.replaceChildren(...this.summaryNodes());
    }

    // The sticky Add to cart on phones (the IIFE at the bottom) owns the button's words and reads this
    syncButtonLabel(active) {
      const button = document.querySelector('.wal-engrave-page product-form button[name="add"]');
      if (!button) return;
      const totals = active ? this.totals() : null;
      let label = '';
      if (totals) label = totals.awards ? `Add ${totals.awards} award${totals.awards === 1 ? '' : 's'}${totals.known ? ` · ${this.api.formatMoney(totals.total)}` : ''}` : 'Add your awards';
      if ((button.dataset.walBatchLabel || '') === label) return;
      if (label) button.dataset.walBatchLabel = label;
      else delete button.dataset.walBatchLabel;
      document.dispatchEvent(new Event('wal:batch-label'));
    }

    formElement() {
      return document.querySelector('.wal-engrave-page product-form form');
    }

    // The "Layout" note for the engraver; in the product form like the quantity box, and removed again when unticked
    syncLayoutInput() {
      const on = this.nameLarger && this.form.engraving();
      if (on && !this.layoutInput) {
        const owner = this.formElement();
        const formId = owner ? owner.getAttribute('id') : this.form.engravingInputs[0].getAttribute('form');
        this.layoutInput = element('input', { type: 'hidden', name: 'properties[Layout]', value: NAME_LARGER, form: formId, 'data-wal-layout': true });
        (owner || this.form.root).append(this.layoutInput);
      } else if (!on && this.layoutInput) {
        this.layoutInput.remove();
        this.layoutInput = null;
      }
    }

    edited() {
      this.hideError();
      this.form.update();
      this.form.onChange();
    }

    showError(error) {
      const rowNode = error.row == null ? null : this.rowsList.children[error.row];
      this.error.textContent = error.message;
      this.error.hidden = false;
      let target;
      if (rowNode) {
        rowNode.append(this.error);
        target = rowNode.querySelector(error.field === 'name' ? '[data-wal-batch-name]' : error.field === 'colour' ? '[data-wal-batch-colour]' : '[data-wal-batch-award]');
        if (!target || target.disabled) target = rowNode.querySelector('[data-wal-batch-award]');
        this.current = error.row;
        this.form.update();
      } else {
        this.sharedList.after(this.error);
        target = this.sharedList.querySelector('[data-wal-shared-input]') || this.addRowButton;
      }
      target.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
      target.focus({ preventScroll: true });
    }

    hideError() {
      this.error.hidden = true;
    }

    // --- Adding to the cart ---

    // Called through the lines provider in start() with the add-to-cart form the theme was about to post: one line per award, each
    // with the add-ons it needs (newest first, so the cart reads award 1 to N top to bottom), or an error response to show.
    // engraving-options.js posts them as one cart/add.js request, each add-on a nested child line of its award.
    lines(formData) {
      const result = this.validateNow();
      if (!result.ok) return jsonResponse(422, { status: 422, message: 'Check your awards', description: result.errors[0].message });
      const items = this.api.buildItems(formData.entries(), this.rows, {
        shared: this.shared,
        neededBy: this.api.formatNeededBy(this.neededBy),
        nameLarger: this.nameLarger,
        colourAll: !this.colourSome(),
      });
      if (!items.length) return jsonResponse(422, { status: 422, message: 'Check your awards', description: 'Add at least one award to the list' });
      return items;
    }

    added() {
      this.form.onTrack('engraving_batch', { action: 'added', ...this.counts() });
    }

    // --- Drafts ---

    toDraft() {
      const text = (value) => String(value == null ? '' : value).slice(0, MAX_LINE_LENGTH);
      return {
        mode: this.mode,
        shared: this.shared.map((item) => (item.type === 'text' ? { type: 'text', value: text(item.value), label: item.label, placeholder: item.placeholder } : { type: item.type })),
        rows: this.rows.map((row) => ({ award: text(row.award), name: text(row.name), tbc: Boolean(row.tbc), qty: this.api.qtyOf(row), colour: Boolean(row.colour) })),
        colourAll: this.colourAll,
        neededBy: this.neededBy,
        nameLarger: this.nameLarger,
        current: this.current,
      };
    }

    fromDraft(saved) {
      if (!saved || typeof saved !== 'object') return;
      const text = (value) => String(value == null ? '' : value).slice(0, MAX_LINE_LENGTH);
      if (Array.isArray(saved.shared)) {
        const items = saved.shared.filter((item) => item && ['text', 'award', 'name'].includes(item.type)).slice(0, this.maxLines);
        const slots = (type) => items.filter((item) => item.type === type).length;
        if (slots('award') === 1 && slots('name') === 1) {
          this.shared = items.map((item) => (item.type === 'text' ? this.textLine(text(item.label) || 'Extra line', text(item.placeholder), text(item.value)) : { type: item.type, key: this.nextKey() }));
        }
      }
      if (Array.isArray(saved.rows) && saved.rows.length) {
        this.rows = saved.rows.slice(0, this.max).map((row) => ({ award: text(row && row.award), name: text(row && row.name), tbc: Boolean(row && row.tbc), qty: this.api.qtyOf(row || {}), colour: Boolean(row && row.colour) }));
      }
      this.colourAll = saved.colourAll !== false;
      this.neededBy = typeof saved.neededBy === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(saved.neededBy) && saved.neededBy >= isoToday() ? saved.neededBy : '';
      this.nameLarger = Boolean(saved.nameLarger);
      this.current = Math.max(0, Math.min(parseInt(saved.current, 10) || 0, this.rows.length - 1));
      this.mode = saved.mode === 'batch' ? 'batch' : 'one';
      this.renderShared();
      this.renderRows();
    }

    teardown() {
      this.checkoutBlock.remove();
      this.colourScope.remove();
      if (this.desktop) this.desktop.removeEventListener('change', this.onDesktopChange);
      if (this.onQuantity) document.removeEventListener('change', this.onQuantity);
      if (this.stage) this.stage.remove();
      document.documentElement.classList.remove('wal-batch-on');
      if (this.layoutInput) this.layoutInput.remove();
      this.layoutInput = null;
      const button = document.querySelector('.wal-engrave-page product-form button[name="add"]');
      if (button && button.dataset.walBatchLabel) {
        delete button.dataset.walBatchLabel;
        document.dispatchEvent(new Event('wal:batch-label'));
      }
    }
  }

  // What the gift set form (gift-set-form.js, loaded after this file) borrows: the wording, lettering fonts and helpers of the single-piece forms
  window.WalFormKit = {
    element, previewFont, fontDisplayName, KINDS, GLASS_OCCASIONS, MAX_LINE_LENGTH, LOGO_ONLY, shieldIcon, logoDelay, plainLetters,
    exampleDate, shortDate, year, setText, reducedMotion, storage, readDraft,
  };

  const start = (container) => {
    const configNode = document.getElementById('wal-engrave-config');
    const config = configNode ? JSON.parse(configNode.textContent) : {};
    // The gift sets have a form of their own (gift-set-form.js)
    if (config.kind === 'gift-set') return;
    const kind = kindOf(config);
    fontPopularity = kind.fonts;
    // PostHog: how many start engraving, hit an error, or use a toast, per product type (Add to cart is tracked already)
    const track = (event, properties = {}) => {
      if (window.posthog && window.posthog.capture) window.posthog.capture(event, { form: config.kind || 'quaich', product_id: config.productId, ...properties });
    };
    let startTracked = false;
    const productId = config.productId || (window.ShopifyAnalytics && ShopifyAnalytics.meta.product && ShopifyAnalytics.meta.product.id) || location.pathname;
    const draftKey = `wal-engrave:v1:${productId}`;
    let form = null;
    let draft = readDraft(draftKey);
    let restored = false;
    let customerChanged = false;
    let saveTimer;

    const stopRestoring = () => {
      restoringDraft = false;
    };

    const save = () => {
      clearTimeout(saveTimer);
      if (!form || !form.intact() || restoringDraft || !customerChanged) return;
      const lines = form.lineValues.slice(0, form.maxLines).map((line) => line.slice(0, MAX_LINE_LENGTH));
      if (!form.engraving() && !lines.some((line) => line.trim())) return storage(() => localStorage.removeItem(draftKey));
      const checkedValue = (name) => (form.checked(name) || {}).value;
      const notes = form.fields.notes && form.fields.notes.querySelector('input, textarea');
      const saved = {
        savedAt: Date.now(),
        engraving: checkedValue('engraving'),
        font: checkedValue('font'),
        lines,
        occasion: form.occasion,
        pairMode: form.pairMode,
        secondLines: form.secondLineValues.slice(0, form.maxLines),
        logo: checkedValue('logo'),
        logoFile: window.WalArtwork.toDraft(),
        infill: checkedValue('infill'),
        note: notes ? notes.value : '',
        batch: form.batch ? form.batch.toDraft() : undefined,
      };
      storage(() => localStorage.setItem(draftKey, JSON.stringify(saved)));
    };

    const changed = () => {
      if (restoringDraft) return;
      customerChanged = true;
      clearTimeout(saveTimer);
      saveTimer = setTimeout(save, 300);
    };

    // Puts back what the customer chose last time.
    const restore = (attempt = 0) => {
      if (!draft || customerChanged) return stopRestoring();
      const retry = () => (attempt < 30 ? setTimeout(() => restore(attempt + 1), 100) : stopRestoring());
      if (!form || !form.intact()) return retry();
      restoringDraft = true;

      const choice =
        form.engravingInputs.find((input) => input.value === draft.engraving) ||
        form.engravingInputs.find((input) => draft.engraving !== 'No' && input.value !== 'No');
      if (choice && !choice.checked && !form.textarea.value.trim()) choice.click();
      if (draft.engraving !== 'No' && !form.fontInputs().length) return retry();
      if (form.textarea.value.trim()) return stopRestoring();

      const font = form.fontInputs().find((input) => input.value === draft.font);
      if (font) {
        fontDefaultDone = true;
        font.click();
      }
      if (Array.isArray(draft.lines)) {
        const saved = draft.lines.map((line) => (typeof line === 'string' ? line : ''));
        // A draft from a quaich that takes more lines: extra lines join the last one rather than disappearing
        if (saved.length > form.maxLines) saved.splice(form.maxLines - 1, saved.length, saved.slice(form.maxLines - 1).filter((line) => line.trim()).join(' '));
        form.lineValues = saved.map((line) => line.slice(0, MAX_LINE_LENGTH));
        form.writeTextarea();
      }
      if (kind.occasions.some((item) => item.id === draft.occasion)) form.occasion = draft.occasion;
      const logo = form.fields.logo && [...form.fields.logo.querySelectorAll('input[type="radio"]')].find((input) => input.value === draft.logo);
      if (logo) logo.click();
      // The uploaded file's link doesn't expire, so a logo from last time can come back with the words
      if (logo && draft.logo !== 'No' && draft.logoFile) window.WalArtwork.restore(draft.logoFile);
      const infill = form.infillInputs().find((input) => input.value === draft.infill);
      if (infill) infill.click();
      if (form.batch && draft.batch) form.batch.fromDraft(draft.batch);
      const notes = form.fields.notes && form.fields.notes.querySelector('input, textarea');
      if (notes && typeof draft.note === 'string') {
        notes.value = draft.note;
        dispatchInputEvents(notes);
      }
      if (config.pair) {
        form.pairMode = draft.pairMode === 'different' ? 'different' : 'same';
        form.secondLineValues = Array.isArray(draft.secondLines) ? draft.secondLines.map((line) => String(line).slice(0, MAX_LINE_LENGTH)).slice(0, form.maxLines) : [];
        form.renderSecond();
      }
      form.renderLines();
      form.update();
      stopRestoring();
    };

    window.addEventListener('pagehide', save);
    document.addEventListener('visibilitychange', () => document.visibilityState === 'hidden' && save());

    // Once this quaich is in the cart its draft is done with, so adding another starts fresh.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source !== 'product-form') return;
        const variant = document.querySelector('.wal-engrave-page product-form input[name="id"]');
        if (variant && String(event.productVariantId) !== variant.value) return;
        clearTimeout(saveTimer);
        customerChanged = false;
        draft = null;
        storage(() => localStorage.removeItem(draftKey));
      });
    }

    if (config.kind === 'glass-award') {
      // "Several awards": the add to cart becomes one line per award (engraving-options.js adds each one's add-ons)
      window.WalEngraving.setLinesProvider(
        (formData) => (form && form.intact() && form.batchActive() ? form.batch.lines(formData) : null),
        () => form && form.batch && form.batch.added()
      );
      // Choosing another size changes the price and the size note (the theme dispatches change on the product form's id input)
      document.addEventListener('change', (event) => {
        if (form && form.intact() && event.target.matches && event.target.matches('product-form input[name="id"]')) form.update();
      });
    }

    const attach = () => {
      const next = new QuaichEngraving(container, config);
      if (!next.find()) {
        window.WalArtwork.release('custom');
        window.WalEngraving.release('custom');
        return;
      }
      try {
        next.onChange = changed;
        next.onTrack = track;
        if (draft) restoringDraft = true;
        next.build();
        form = next;
        if (draft) {
          restored = true;
          restore();
        }
      } catch (error) {
        console.error('Engraving form: falling back to the simple form', error);
        next.teardown();
        window.WalEngraving.release('custom');
        form = null;
      }
    };
    window.WalEngraving.claim('custom');
    attach();

    // A tap or key press mid-restore means the customer has taken over.
    const interruptRestore = () => {
      if (!restoringDraft) return;
      stopRestoring();
      changed();
    };
    container.addEventListener('pointerdown', interruptRestore, true);
    container.addEventListener('keydown', interruptRestore, true);
    container.addEventListener('change', (event) => {
      if (!applyingDefaultFont && !restoringDraft && form && form.fields.font && form.fields.font.contains(event.target)) fontDefaultDone = true;
      if (form) form.update();
      if (event.isTrusted && form) changed();
      if (event.isTrusted && form && !startTracked && form.fields.engraving.contains(event.target) && form.engraving()) {
        startTracked = true;
        track('engraving_started');
      }
    });
    // The price box follows the quantity and the chosen size
    document.addEventListener('change', (event) => {
      if (form && form.intact() && event.target.matches && event.target.matches('.wal-engrave-page input[name="quantity"], product-form input[name="id"]')) form.update();
    });
    container.addEventListener('input', (event) => {
      if (form && form.fields.notes && form.fields.notes.contains(event.target)) form.update();
      if (event.isTrusted && form) changed();
    });

    // Never let an engraved piece go to the cart without words (or a logo still to come): checked on the Add to cart click and on a
    // submit by other means (Enter in the note box)
    const guard = (event) => {
      if (!form || !form.intact() || !form.engraving()) return;
      window.WalArtwork.sync();
      if (form.logoOn() && form.uploadState() !== 'done') {
        event.preventDefault();
        event.stopImmediatePropagation();
        form.showError(form.logoError(), form.logoPanel);
        track('engraving_error', { field: 'logo' });
        return;
      }
      if (form.batchActive()) {
        // Award 1's words go into the Text box; the whole list is checked here
        const problem = form.batch.prepare();
        if (!problem) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        form.batch.showError(problem);
        track('engraving_error', { field: 'batch' });
        return;
      }
      const pairError = form.pairError();
      if (pairError) {
        event.preventDefault();
        event.stopImmediatePropagation();
        form.showError(pairError, form.fields.text);
        if (pairError.includes('Glass 2')) form.secondInputs[0].focus();
        return;
      }
      if (form.textarea.value.trim()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      form.showError(TEXT_ERROR, form.fields.text);
      track('engraving_error', { field: 'text' });
    };
    document.addEventListener('click', (event) => event.target.closest && event.target.closest('product-form button[name="add"]') && guard(event), true);
    document.addEventListener('submit', (event) => event.target.closest && event.target.closest('product-form') && guard(event), true);
  };

  const waitForContainer = () => {
    const found = document.getElementById('wal-options');
    if (found && window.WalEngraving) return start(found);
    // engraving-options.js is deferred ahead of this file; if it has not rendered yet it announces itself
    document.addEventListener(
      'wal:options-ready',
      () => {
        const container = document.getElementById('wal-options');
        if (container) start(container);
      },
      { once: true }
    );
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', waitForContainer);
  else waitForContainer();
})();

// Dispatch and delivery promise above Add to cart, worked out in UK time from the Engraving form theme settings.
// Rewrites the texts quaich-dispatch.liquid rendered (same wording) in case a cached page has crossed the cutoff.
(() => {
  const DAY = 24 * 60 * 60 * 1000;
  const iso = (date) => date.toISOString().slice(0, 10);
  // "Thu 8 Oct", as Liquid's '%a %-d %b' (en-GB in browsers writes "Sept")
  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const format = (date) => `${WEEKDAYS[date.getUTCDay()]} ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;
  const count = (value, fallback) => {
    const number = parseInt(value, 10);
    return Number.isNaN(number) ? fallback : Math.max(0, number);
  };

  const init = (panel) => {
    const usualDays = count(panel.dataset.usual, 0);
    const logoDays = count(panel.dataset.logoDays, 0);
    const cutoff = count(panel.dataset.cutoff, 10);
    const closed = new Set((panel.dataset.closed || '').split(/\s+/).filter((line) => /^\d{4}-\d{2}-\d{2}$/.test(line)));
    const isWorkingDay = (date) => date.getUTCDay() % 6 !== 0 && !closed.has(iso(date));
    const nextWorkingDay = (date) => {
      let next = new Date(date.getTime() + DAY);
      while (!isWorkingDay(next)) next = new Date(next.getTime() + DAY);
      return next;
    };
    const addWorkingDays = (date, steps) => {
      let result = date;
      for (let i = 0; i < steps; i += 1) result = nextWorkingDay(result);
      return result;
    };
    const weekdaysAfter = (date, steps) => {
      let result = date;
      for (let i = 0; i < steps; i += 1) {
        do result = new Date(result.getTime() + DAY);
        while (result.getUTCDay() % 6 === 0);
      }
      return result;
    };

    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' })
        .formatToParts(new Date())
        .map((part) => [part.type, part.value])
    );
    const today = new Date(Date.UTC(+parts.year, +parts.month - 1, +parts.day));
    const tomorrow = new Date(today.getTime() + DAY);
    const dayText = (date) => {
      if (iso(date) === iso(today)) return 'today';
      if (iso(date) === iso(tomorrow)) return 'tomorrow';
      return format(date);
    };

    const start = isWorkingDay(today) && +parts.hour < cutoff ? today : nextWorkingDay(today);
    const cutoffLabel = cutoff < 12 ? `${cutoff}am` : cutoff === 12 ? '12 noon' : `${cutoff - 12}pm`;

    const set = (selector, text) => {
      const node = panel.querySelector(selector);
      if (node && node.textContent !== text) node.textContent = text;
    };
    // A logo is redrawn as an engraving file by an outside digitiser first, which puts the posting date back
    const render = (logo) => {
      const extra = logo ? logoDays : 0;
      const usual = addWorkingDays(start, usualDays + extra);
      const standard = weekdaysAfter(usual, count(panel.dataset.standard, 3));
      const express = weekdaysAfter(usual, count(panel.dataset.express, 1));
      let explain = iso(usual) === iso(today) ? `Order by ${cutoffLabel} UK time` : '';
      if (extra) explain = `Logos take ${extra} extra working ${extra === 1 ? 'day' : 'days'} to prepare`;

      set('[data-wal-headline]', `Usually posted ${dayText(usual)}`);
      set('[data-wal-explain]', explain);
      set('[data-wal-standard]', dayText(standard));
      set('[data-wal-express]', dayText(express));
      set('[data-wal-collect]', dayText(usual));
    };

    render(false);
    if (!logoDays) return;
    const watch = () => {
      const artwork = window.WalArtwork;
      if (!artwork) return;
      render(artwork.state.active);
      artwork.subscribe((current, event) => {
        if (event === 'active') render(current.active);
      });
    };
    // artwork-upload.js is deferred ahead of this file, but if it is missing here it may still arrive
    if (window.WalArtwork) watch();
    else window.addEventListener('load', watch, { once: true });
  };

  const run = () => document.querySelectorAll('[data-wal-dispatch-settings]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();

// "Read more" on quaich and whisky glass descriptions: the split is done in Liquid, this only reveals the rest.
(() => {
  const run = () =>
    document.querySelectorAll('[data-wal-read-more]').forEach((button) => {
      const rest = button.parentElement.querySelector('.wal-read-more__rest');
      const clamped = button.previousElementSibling && button.previousElementSibling.classList.contains('wal-clamp--lines') ? button.previousElementSibling : null;
      if (clamped && clamped.scrollHeight <= clamped.clientHeight + 4) {
        clamped.classList.remove('wal-clamp--lines');
        button.remove();
        return;
      }
      button.addEventListener('click', () => {
        if (rest) rest.hidden = false;
        if (clamped) clamped.classList.remove('wal-clamp--lines');
        button.remove();
      });
    });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();


// Phones: once engraving is chosen, the real Add to cart rides along at the bottom of the screen (CSS position: sticky)
// while the customer is above it, then settles into its usual place under the delivery box. This only labels the button
// and tells the CSS when it is floating, so it can sit narrower then (Safari 26 tints its toolbar from sticky elements
// 80% of the screen wide or more) and widen as it lands.
(() => {
  const PHONE = window.matchMedia('(max-width: 749px)');
  const run = () => {
    const button = document.querySelector('.wal-engrave-page product-form button[name="add"]');
    const dock = button && button.closest('.buy-buttons');
    if (!dock) return;
    // A span, because Dawn hides empty divs
    const marker = document.createElement('span');
    marker.style.cssText = 'display: block; margin: 0 !important; height: 0;';
    marker.setAttribute('aria-hidden', 'true');
    dock.after(marker);
    const labelNode = button.querySelector('span');
    const original = labelNode.textContent.trim();

    // Gift sets: the pieces ticked and the words still missing come from the gift set form (assets/gift-set-form.js)
    const gift = () => (window.WalEngraving && window.WalEngraving.gift) || null;
    const engraving = () => {
      if (gift()) return window.WalEngraving.engraving();
      const chosen = document.querySelector('.wal-f-engraving input[type="radio"]:checked');
      return Boolean(chosen && chosen.value !== 'No');
    };
    const needsWords = () => {
      if (gift()) return engraving() && gift().needsWords();
      const text = document.querySelector('.wal-f-text textarea');
      return engraving() && Boolean(text) && !text.value.trim();
    };

    const update = () => {
      const active = PHONE.matches && engraving() && !button.disabled;
      document.documentElement.classList.toggle('wal-dock', active);
      const options = window.WalEngraving;
      const total = options ? options.total() : null;
      const amount = total === null ? '' : options.formatMoney(total);
      let text = original;
      if (active) text = needsWords() ? 'Add your words' : amount ? `${original} · ${amount}` : original;
      // Glass awards, "Several awards": "Add 7 awards · £356.00" (set by AwardBatch)
      if (button.dataset.walBatchLabel && !button.disabled) text = button.dataset.walBatchLabel;
      if (labelNode.textContent.trim() !== text) labelNode.textContent = text;
      // Floating when sticky has lifted it above its natural spot, which the marker just after it records
      // (allowing for the product form's bottom margin, which collapses out between the two)
      const restingGap = parseFloat(getComputedStyle(dock.firstElementChild).marginBottom) || 0;
      const floating = active && marker.getBoundingClientRect().top - dock.getBoundingClientRect().bottom > restingGap + 8;
      dock.classList.toggle('is-floating', floating);
    };

    let queued = false;
    const queue = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        update();
      });
    };
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    document.addEventListener('change', queue);
    document.addEventListener('input', queue);
    document.addEventListener('wal:batch-label', queue);
    document.addEventListener('wal:options-change', queue);
    PHONE.addEventListener('change', queue);
    update();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();
